import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { QueryResult, RetrievedCard, ZinsserSessionListItem, AnatomyInsightResponse } from '@/lib/api'
import {
  listZinsserSessions,
  createZinsserSession,
  getZinsserSession,
  getZinsserSessionWithTurns,
  deleteZinsserSession as apiDeleteZSession,
  updateZinsserSession,
  parseAnatomyStructure,
  getAnatomyInsight,
} from '@/lib/api'
import { convertAPIResponse, type ParseResult } from '@/lib/anatomy-parser'
import { computeStreamingDiff, stripAnnotations } from '@/lib/diff'
import { stripFlashMarkers } from '@/components/editor/AnnotationExtension'


// =============================================================================
// CRITICAL: User isolation check - runs SYNCHRONOUSLY before store hydration
// =============================================================================
// This MUST run before the Zustand store is created to prevent data leakage
// between users. We check if the stored user ID matches the current Supabase
// session user ID. If not, we clear the zinsser-store from localStorage.
// =============================================================================

const ZINSSER_USER_ID_KEY = 'zinsser-store-user-id'

// Clear ALL old store versions on module load
// This ensures clean slate when store key changes
function clearOldStoreVersions(): void {
  const keysToRemove = ['zinsser-store', 'zinsser-store-v2']
  keysToRemove.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      console.log('[zinsser-store] Removing old store version:', key)
      localStorage.removeItem(key)
    }
  })
}
clearOldStoreVersions()

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(ZINSSER_USER_ID_KEY)
    const hasZStore = localStorage.getItem('zinsser-store-v3') !== null

    // Find Supabase auth token in localStorage
    let currentUserId: string | null = null

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const rawValue = localStorage.getItem(key)
          const authData = JSON.parse(rawValue || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch {
          // Invalid JSON, skip
        }
      }
    }

    // CASE 1: User ID mismatch
    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      localStorage.removeItem('zinsser-store-v3')
      localStorage.setItem(ZINSSER_USER_ID_KEY, currentUserId)
    }
    // CASE 2: No stored user ID but we have store data AND a current user
    else if (!storedUserId && hasZStore && currentUserId) {
      localStorage.removeItem('zinsser-store-v3')
      localStorage.setItem(ZINSSER_USER_ID_KEY, currentUserId)
    }
    // CASE 3: New user, fresh start
    else if (!storedUserId && currentUserId) {
      localStorage.setItem(ZINSSER_USER_ID_KEY, currentUserId)
    }
    // CASE 4: No current user - clear orphaned data
    else if (!currentUserId && hasZStore) {
      localStorage.removeItem('zinsser-store-v3')
      localStorage.removeItem(ZINSSER_USER_ID_KEY)
    }
  } catch {
    // Ignore errors
  }
}

// Run the check immediately when this module is loaded
clearStoreIfUserMismatch()

// =============================================================================

export interface RagQuery {
  query: string
  reasoning: string
  priority: number
  target_card_types?: string[]
}

export interface FinalDiffData {
  has_changes: boolean
  original_doc: string
  final_doc: string
  stats: {
    blocks_added: number
    blocks_deleted: number
    blocks_modified: number
    blocks_unchanged: number
  }
  changes: Array<{
    type: 'added' | 'deleted' | 'modified'
    content?: string
    modifications?: Array<{
      deleted: string
      added: string
      context: string
    }>
  }>
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  thinking?: string  // Extended thinking content (collapsed after streaming)
  zinsserThinking?: string  // Zinsser (macro editing) thinking for this turn
  cutClutterThinking?: string  // CutClutter (micro editing - Pass 1) thinking for this turn
  strengthenVerbsThinking?: string  // StrengthenVerbs (micro editing - Pass 2) thinking for this turn
  checkLogicThinking?: string  // CheckLogic (micro editing - Pass 3) thinking for this turn
  rhythmSoundThinking?: string  // RhythmSound (micro editing - Pass 4) thinking for this turn
  rhythmThinking?: string  // Rhythm standalone (context menu) thinking for this turn
  voiceThinking?: string  // Voice standalone (toolbar button) thinking for this turn
  responderThinking?: string  // Responder (coaching response) thinking for this turn
  // Node execution flags (for showing indicators even without thinking content)
  zinsserRan?: boolean
  cutClutterRan?: boolean
  strengthenVerbsRan?: boolean
  checkLogicRan?: boolean
  rhythmSoundRan?: boolean
  rhythmRan?: boolean  // Rhythm standalone (context menu) ran for this turn
  voiceRan?: boolean  // Voice standalone (toolbar button) ran for this turn
  responderRan?: boolean
  // Final diff for this turn (shown after editing completes)
  finalDiff?: FinalDiffData | null
  showFinalDiff?: boolean  // Whether to show the diff viewer for this turn
  // Anatomy insight (from sentence/paragraph analysis)
  isAnatomyInsight?: boolean
  anatomyScope?: 'sentence' | 'paragraph'
  anatomySourceText?: string  // The sentence/paragraph that was analyzed
}

export interface ZResponse {
  coaching_message: string
  question: string | null
  current_queries: RagQuery[]
  narrative_direction: string
  key_themes: string[]
  summary: string | null
  ready_for_retrieval: boolean
  session_id: string
  turn_count: number
  thinking?: string  // Extended thinking content
}

export interface ZSubmitResponse {
  message: string
  session_id: string
  event_id: string
  task_id: string
  workflow: string
  force_summary: boolean
}

type WorkflowStatus = 'idle' | 'submitting' | 'processing' | 'streaming' | 'complete' | 'error'
export type ZinsserMode = 'brainstorm' | 'foolscap' | 'draft' | 'gatekeeper' | 'responder'
export type RunMode = 'fast' | 'deep'

export interface GatekeeperStreamingContent {
  response: string
  session_id?: string
  thinking?: string
}

export interface BrainstormStreamingContent {
  coaching_message: string
  // Document content with ==additions== and ~~deletions~~ annotations embedded by AI
  new_doc?: string
  old_doc?: string  // Clean content for DB
  last_agent_brainstorm_output?: string  // Frozen snapshot for edit detection
  brainstorm_modified?: boolean
  // Scratchpad content (agent's working notes)
  scratchpad_content?: string
  new_scratchpad_content?: string  // Clean scratchpad for DB
  scratchpad_modified?: boolean
  session_context?: {
    phase?: string
    context_summary?: string
    audience_understood?: boolean
    purpose_understood?: boolean
    key_insights?: string[]
  } | null
  // Session name suggestion from AI
  suggested_session_name?: string | null
  // Extended thinking content from the model
  thinking?: string
  // Session ID
  session_id?: string
  // Whether this node was skipped
  skipped?: boolean
}

export interface FoolscapStreamingContent {
  coaching_message: string
  question?: string | null
  foolscap_content?: string
  foolscap_modified?: boolean
  scratchpad_content?: string
  scratchpad_modified?: boolean
  session_context?: {
    phase?: string
    context_summary?: string
    structural_gaps?: string[]
  } | null
  // Finalized brainstorm content (annotations stripped) - sent on first foolscap turn
  finalized_brainstorm_content?: string
  // Extended thinking content from the model
  thinking?: string
}

interface ZState {
  // Session
  sessionId: string | null
  sessionName: string

  // Session list (for picker)
  sessions: ZinsserSessionListItem[]
  isLoadingSessions: boolean
  showSessionPicker: boolean

  // Session sidebar (left panel)
  showSessionSidebar: boolean

  // Mobile view state (for single-view mobile layout)
  mobileView: 'sessions' | 'chat' | 'document'

  // Z mode (brainstorm | foolscap | draft)
  zinsserMode: ZinsserMode

  // Run mode (fast | deep) - controls extended thinking
  runMode: RunMode

  // Conversation
  turns: Turn[]
  currentQueries: RagQuery[]
  narrativeDirection: string
  keyThemes: string[]
  phase: 'interviewing' | 'ready_for_retrieval'

  // Workflow status
  workflowStatus: WorkflowStatus
  isStandaloneWorkflow: boolean  // True when running micro-editing via context menu (no Gatekeeper)
  currentTaskId: string | null
  error: string | null
  noChangesMessage: string | null  // Temporary message when micro-editing makes no changes

  // Streaming state
  streamingContent: Partial<ZResponse> | null
  streamingThinking: string | null  // Extended thinking being streamed
  isThinking: boolean  // Whether currently streaming thinking

  // Retrieval results
  queryResults: QueryResult[]
  totalCards: number
  isRetrieving: boolean

  // Brainstorm state
  brainstormMode: boolean
  brainstormContent: string  // Annotated content for display (with ==additions== ~~deletions~~)
  brainstormCleanContent: string  // Clean content for next turn diff (no annotations)
  brainstormDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  brainstormPhase: string
  brainstormWorkingDirection: string
  brainstormKeyThemes: string[]
  brainstormGaps: string[]
  brainstormTurns: Turn[]
  brainstormStreamingContent: BrainstormStreamingContent | null
  brainstormStreamingThinking: string | null
  brainstormIsThinking: boolean
  // Streaming document state
  brainstormStreamingDocument: string  // Accumulating streamed document lines
  brainstormDocumentStreaming: boolean  // Whether document is currently streaming
  brainstormDocumentPending: boolean  // Whether document update is pending
  brainstormStreamingLineCount: number  // Current line count for progress indicator

  // Foolscap state
  foolscapContent: string
  foolscapCleanContent: string  // Clean content for next turn diff (no annotations)
  foolscapDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  foolscapPhase: string
  foolscapStreamingContent: FoolscapStreamingContent | null
  foolscapStreamingThinking: string | null
  foolscapIsThinking: boolean
  // Streaming document state
  foolscapStreamingDocument: string  // Accumulating streamed document lines
  foolscapDocumentStreaming: boolean  // Whether document is currently streaming
  foolscapDocumentPending: boolean
  foolscapStreamingLineCount: number  // Current line count for progress indicator

  // Scratchpad state (agent's working notes)
  scratchpadContent: string
  scratchpadCleanContent: string  // Clean content for next turn diff (no annotations)
  scratchpadDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  // Streaming scratchpad state
  scratchpadStreamingDocument: string  // Accumulating streamed scratchpad lines
  scratchpadDocumentStreaming: boolean  // Whether scratchpad is currently streaming
  scratchpadStreamingLineCount: number  // Current line count for progress indicator

  // Gatekeeper state (interview + routing)
  gatekeeperTurns: Turn[]
  gatekeeperStreamingContent: GatekeeperStreamingContent | null
  gatekeeperStreamingThinking: string | null
  gatekeeperIsThinking: boolean

  // Zinsser state (macro editing)
  zinsserDoc: string  // The macro-edited document from Zinsser
  zinsserDocAnnotated: string  // Annotated document with <ins>/<del> tags
  showAnnotations: boolean  // Whether to show annotations overlay
  zinsserDiagnosis: {
    controlling_idea?: string
    audience_contract?: string
    structure_issues?: string[]
    evidence_gaps?: string[]
    opening_closing?: string
    voice_consistency?: string
  } | null
  zinsserChangesMade: string[]
  zinsserStreamingThinking: string | null
  zinsserIsThinking: boolean
  zinsserIsProcessing: boolean  // Whether Zinsser is currently working
  zinsserHasThinking: boolean   // Whether Zinsser ran this turn (for showing collapsed drawer)

  // CutClutter state (micro editing - Pass 1)
  cutClutterChangesMade: string[]
  cutClutterStreamingThinking: string | null
  cutClutterIsThinking: boolean
  cutClutterIsProcessing: boolean  // Whether CutClutter is currently working
  cutClutterHasThinking: boolean   // Whether CutClutter ran this turn

  // StrengthenVerbs state (micro editing - Pass 2)
  strengthenVerbsChangesMade: string[]
  strengthenVerbsStreamingThinking: string | null
  strengthenVerbsIsThinking: boolean
  strengthenVerbsIsProcessing: boolean  // Whether StrengthenVerbs is currently working
  strengthenVerbsHasThinking: boolean   // Whether StrengthenVerbs ran this turn

  // CheckLogic state (micro editing - Pass 3)
  checkLogicChangesMade: string[]
  checkLogicStreamingThinking: string | null
  checkLogicIsThinking: boolean
  checkLogicIsProcessing: boolean  // Whether CheckLogic is currently working
  checkLogicHasThinking: boolean   // Whether CheckLogic ran this turn

  // RhythmSound state (micro editing - Pass 4)
  rhythmSoundChangesMade: string[]
  rhythmSoundStreamingThinking: string | null
  rhythmSoundIsThinking: boolean
  rhythmSoundIsProcessing: boolean  // Whether RhythmSound is currently working
  rhythmSoundHasThinking: boolean   // Whether RhythmSound ran this turn

  // Rhythm standalone state (right-click context menu - bypasses chat)
  rhythmChangesMade: string[]
  rhythmStreamingThinking: string | null
  rhythmIsThinking: boolean
  rhythmIsProcessing: boolean  // Whether standalone Rhythm is currently working
  rhythmHasThinking: boolean   // Whether standalone Rhythm ran this turn

  // Voice standalone state (toolbar button - bypasses chat, operates on full document)
  voiceChangesMade: string[]
  voiceStreamingThinking: string | null
  voiceIsThinking: boolean
  voiceIsProcessing: boolean  // Whether standalone Voice is currently working
  voiceHasThinking: boolean   // Whether standalone Voice ran this turn

  // Responder state (coaching response after editing)
  responderResponse: string | null
  responderFollowUpQuestion: string | null
  responderStreamingThinking: string | null
  responderIsThinking: boolean
  responderIsProcessing: boolean  // Whether Responder is currently working
  responderHasThinking: boolean   // Whether Responder ran this turn

  // Final diff state (shown after workflow complete)
  finalDiff: {
    has_changes: boolean
    original_doc: string
    final_doc: string
    stats: {
      blocks_added: number
      blocks_deleted: number
      blocks_modified: number
      blocks_unchanged: number
    }
    changes: Array<{
      type: 'added' | 'deleted' | 'modified'
      content?: string
      modifications?: Array<{
        deleted: string
        added: string
        context: string
      }>
    }>
  } | null
  showFinalDiff: boolean  // Whether to show the diff viewer

  // Attachment refresh trigger (incremented when attachments change)
  attachmentVersion: number

  // Actions
  setZinsserMode: (mode: ZinsserMode) => void
  setRunMode: (mode: RunMode) => void
  setSessionName: (name: string) => void
  addUserTurn: (content: string) => void
  addAssistantTurn: (response: ZResponse) => void
  setWorkflowStatus: (status: WorkflowStatus) => void
  setCurrentTaskId: (taskId: string | null) => void
  setError: (error: string | null) => void
  clearNoChangesMessage: () => void
  startNewSession: () => void
  loadSession: (sessionId: string, turns: Turn[], queries: RagQuery[], direction: string, themes: string[]) => void

  // Session management actions
  openSessionPicker: () => void
  closeSessionPicker: () => void
  openSessionSidebar: () => void
  closeSessionSidebar: () => void
  toggleSessionSidebar: () => void
  setMobileView: (view: 'sessions' | 'chat' | 'document') => void
  fetchSessions: () => Promise<void>
  createNewSession: (name?: string) => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>
  refreshSessionNameIfNeeded: () => Promise<void>

  // Streaming actions
  startStreaming: () => void
  updateStreamingContent: (chunk: Partial<ZResponse>) => void
  updateStreamingThinking: (delta: string) => void
  startThinking: () => void
  finishThinking: () => void
  commitStreamingMessage: () => void  // Convert streaming to real message, unlock input
  finishStreaming: (response: ZResponse) => void

  // Retrieval actions
  setIsRetrieving: (isRetrieving: boolean) => void
  setQueryResults: (results: QueryResult[], total: number) => void
  clearQueryResults: () => void

  // Brainstorm actions
  enterBrainstormMode: () => void
  exitBrainstormMode: () => void
  updateBrainstormContent: (content: string) => void
  addBrainstormTurn: (role: 'user' | 'assistant', content: string) => void
  addBrainstormUserTurn: (content: string) => void
  setBrainstormPhase: (phase: string) => void
  setBrainstormWorkingDirection: (direction: string) => void
  setBrainstormKeyThemes: (themes: string[]) => void
  setBrainstormGaps: (gaps: string[]) => void

  // Brainstorm streaming actions
  startBrainstormStreaming: () => void
  updateBrainstormStreamingContent: (chunk: Partial<BrainstormStreamingContent>) => void
  setBrainstormCoachingMessage: (message: string) => void  // SET (not append) for complete messages
  startBrainstormThinking: () => void
  updateBrainstormThinking: (delta: string) => void
  finishBrainstormThinking: () => void
  commitBrainstormStreamingMessage: () => void
  finishBrainstormStreaming: (output: BrainstormStreamingContent) => void
  // Document streaming - line-by-line streaming with frontend diff computation
  startBrainstormDocumentStreaming: () => void
  appendBrainstormDocumentLine: (line: string) => void
  finishBrainstormDocumentStreaming: (fullDocument: string) => void
  // Legacy: Document update - receives complete annotated document from backend
  updateBrainstormDocumentInstant: (content: string) => void

  // Foolscap actions
  updateFoolscapContent: (content: string) => void
  startFoolscapStreaming: () => void
  updateFoolscapStreamingContent: (chunk: Partial<FoolscapStreamingContent>) => void
  setFoolscapCoachingMessage: (message: string) => void  // SET (not append) for complete messages
  startFoolscapThinking: () => void
  updateFoolscapThinking: (delta: string) => void
  finishFoolscapThinking: () => void
  commitFoolscapStreamingMessage: () => void
  finishFoolscapStreaming: (output: FoolscapStreamingContent & { new_foolscap_content?: string; new_scratchpad_content?: string }) => void
  // Document streaming - line-by-line streaming with frontend diff computation
  startFoolscapDocumentStreaming: () => void
  appendFoolscapDocumentLine: (line: string) => void
  finishFoolscapDocumentStreaming: (fullDocument: string) => void
  // Legacy: Document update - receives complete annotated document from backend
  updateFoolscapDocumentInstant: (content: string) => void

  // Scratchpad streaming actions
  updateScratchpadContent: (content: string) => void
  startScratchpadDocumentStreaming: () => void
  appendScratchpadDocumentLine: (line: string) => void
  finishScratchpadDocumentStreaming: (fullScratchpad: string) => void

  // Gatekeeper actions
  addGatekeeperUserTurn: (content: string) => void
  startGatekeeperStreaming: () => void
  startGatekeeperThinking: () => void
  updateGatekeeperThinking: (delta: string) => void
  finishGatekeeperThinking: () => void
  finishGatekeeperStreaming: (output: GatekeeperStreamingContent) => void

  // Zinsser actions
  startZinsserProcessing: () => void
  startZinsserThinking: () => void
  updateZinsserThinking: (delta: string) => void
  finishZinsserThinking: () => void
  finishZinsserProcessing: (output: {
    zin_doc: string
    diagnosis: {
      controlling_idea?: string
      audience_contract?: string
      structure_issues?: string[]
      evidence_gaps?: string[]
      opening_closing?: string
      voice_consistency?: string
    }
    changes_made: string[]
  }) => void

  // CutClutter actions (micro editing - Pass 1)
  startCutClutterProcessing: () => void
  startCutClutterThinking: () => void
  updateCutClutterThinking: (delta: string) => void
  finishCutClutterThinking: () => void
  finishCutClutterProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // StrengthenVerbs actions (micro editing - Pass 2)
  startStrengthenVerbsProcessing: () => void
  startStrengthenVerbsThinking: () => void
  updateStrengthenVerbsThinking: (delta: string) => void
  finishStrengthenVerbsThinking: () => void
  finishStrengthenVerbsProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // CheckLogic actions (micro editing - Pass 3)
  startCheckLogicProcessing: () => void
  startCheckLogicThinking: () => void
  updateCheckLogicThinking: (delta: string) => void
  finishCheckLogicThinking: () => void
  finishCheckLogicProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // RhythmSound actions (micro editing - Pass 4)
  startRhythmSoundProcessing: () => void
  startRhythmSoundThinking: () => void
  updateRhythmSoundThinking: (delta: string) => void
  finishRhythmSoundThinking: () => void
  finishRhythmSoundProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // Responder actions (coaching response after editing)
  startResponderProcessing: () => void
  startResponderThinking: () => void
  updateResponderThinking: (delta: string) => void
  finishResponderThinking: () => void
  finishResponderProcessing: (output: {
    response: string
    follow_up_question?: string | null
  }) => void

  // Cut Clutter standalone workflow (right-click context menu - bypasses chat)
  finishStandaloneCutClutter: (output: {
    new_doc: string
    annotated_doc?: string | null
    final_diff?: FinalDiffData | null
  }) => void

  // Strengthen Verbs standalone workflow (right-click context menu - bypasses chat)
  finishStandaloneStrengthenVerbs: (output: {
    new_doc: string
    annotated_doc?: string | null
    final_diff?: FinalDiffData | null
  }) => void

  // CheckLogic standalone workflow actions (right-click context menu - bypasses chat)
  finishStandaloneCheckLogic: (output: {
    new_doc: string
    annotated_doc?: string | null
    final_diff?: FinalDiffData | null
  }) => void

  // Rhythm standalone workflow actions (right-click context menu - bypasses chat)
  startRhythmProcessing: () => void
  startRhythmThinking: () => void
  updateRhythmThinking: (delta: string) => void
  finishRhythmThinking: () => void
  finishStandaloneRhythm: (output: {
    new_doc: string
    annotated_doc?: string | null
    final_diff?: FinalDiffData | null
  }) => void

  // Voice standalone workflow actions (toolbar button - bypasses chat, full document)
  startVoiceProcessing: () => void
  startVoiceThinking: () => void
  updateVoiceThinking: (delta: string) => void
  finishVoiceThinking: () => void
  finishStandaloneVoice: (output: {
    new_doc: string
    annotated_doc?: string | null
    final_diff?: FinalDiffData | null
  }) => void

  // Final diff actions
  setFinalDiff: (diff: {
    has_changes: boolean
    original_doc: string
    final_doc: string
    stats: {
      blocks_added: number
      blocks_deleted: number
      blocks_modified: number
      blocks_unchanged: number
    }
    changes: Array<{
      type: 'added' | 'deleted' | 'modified'
      content?: string
      modifications?: Array<{
        deleted: string
        added: string
        context: string
      }>
    }>
  } | null) => void
  showFinalDiffViewer: () => void
  hideFinalDiffViewer: () => void
  hideTurnDiffViewer: (turnIndex: number) => void

  // Real-time canvas editing (streamed from Zinsser/CutClutter/StrengthenVerbs/CheckLogic/RhythmSound)
  applyCanvasEdit: (oldStr: string, newStr: string) => void
  applyCanvasInsert: (insertLine: number, insertText: string) => void

  // Annotation toggle
  toggleAnnotations: () => void
  setAnnotatedDoc: (annotatedDoc: string) => void

  // Attachment actions
  incrementAttachmentVersion: () => void

  // Anatomy view state
  anatomyStructure: ParseResult | null
  anatomyStructureHash: string
  anatomyStructureLoading: boolean
  anatomyInsightCache: Record<string, AnatomyInsightResponse>
  anatomyInsightLoading: boolean
  fetchAnatomyStructure: (content: string) => Promise<void>
  patchAnatomySentence: (paraIndex: number, sentIndex: number, newText: string) => void
  reparseAnatomyParagraph: (paraIndex: number) => Promise<void>
  fetchAnatomyInsight: (text: string, scope: 'sentence' | 'paragraph', context?: string) => Promise<AnatomyInsightResponse | null>
  addAnatomyInsightToChat: (text: string, scope: 'sentence' | 'paragraph', context?: string) => Promise<void>
  clearAnatomyState: () => void
}

export const useZinsserStore = create<ZState>()(
  persist(
    (set): ZState => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Session',
      sessions: [],
      isLoadingSessions: false,
      showSessionPicker: false,
      showSessionSidebar: false,
      mobileView: 'chat',
      zinsserMode: 'gatekeeper',
      runMode: 'fast',
      turns: [],
      currentQueries: [],
      narrativeDirection: '',
      keyThemes: [],
      phase: 'interviewing',
      workflowStatus: 'idle',
      isStandaloneWorkflow: false,
      currentTaskId: null,
      error: null,
      noChangesMessage: null,

      // Streaming state
      streamingContent: null,
      streamingThinking: null,
      isThinking: false,

      // Retrieval state
      queryResults: [],
      totalCards: 0,
      isRetrieving: false,

      // Brainstorm state - always in brainstorm mode by default
      brainstormMode: true,
      brainstormContent: '',
      brainstormCleanContent: '',
      brainstormDisplayContent: null,  // Transient - not persisted
      brainstormPhase: 'direction_finding',
      brainstormWorkingDirection: '',
      brainstormKeyThemes: [],
      brainstormGaps: [],
      brainstormTurns: [],
      brainstormStreamingContent: null,
      brainstormStreamingThinking: null,
      brainstormIsThinking: false,
      // Streaming document state
      brainstormStreamingDocument: '',
      brainstormDocumentStreaming: false,
      brainstormDocumentPending: false,
      brainstormStreamingLineCount: 0,

      // Foolscap state
      foolscapContent: '',
      foolscapCleanContent: '',
      foolscapDisplayContent: null,  // Transient - not persisted
      foolscapPhase: 'draft_creation',
      foolscapStreamingContent: null,
      foolscapStreamingThinking: null,
      foolscapIsThinking: false,
      // Streaming document state
      foolscapStreamingDocument: '',
      foolscapDocumentStreaming: false,
      foolscapDocumentPending: false,
      foolscapStreamingLineCount: 0,

      // Scratchpad state
      scratchpadContent: '',
      scratchpadCleanContent: '',
      scratchpadDisplayContent: null,  // Transient - not persisted
      // Streaming scratchpad state
      scratchpadStreamingDocument: '',
      scratchpadDocumentStreaming: false,
      scratchpadStreamingLineCount: 0,

      // Gatekeeper state
      gatekeeperTurns: [],
      gatekeeperStreamingContent: null,
      gatekeeperStreamingThinking: null,
      gatekeeperIsThinking: false,

      // Zinsser state
      zinsserDoc: '',
      zinsserDocAnnotated: '',
      showAnnotations: false,
      zinsserDiagnosis: null,
      zinsserChangesMade: [],
      zinsserStreamingThinking: null,
      zinsserIsThinking: false,
      zinsserIsProcessing: false,
      zinsserHasThinking: false,

      // CutClutter state
      cutClutterChangesMade: [],
      cutClutterStreamingThinking: null,
      cutClutterIsThinking: false,
      cutClutterIsProcessing: false,
      cutClutterHasThinking: false,

      // StrengthenVerbs state
      strengthenVerbsChangesMade: [],
      strengthenVerbsStreamingThinking: null,
      strengthenVerbsIsThinking: false,
      strengthenVerbsIsProcessing: false,
      strengthenVerbsHasThinking: false,

      // CheckLogic state
      checkLogicChangesMade: [],
      checkLogicStreamingThinking: null,
      checkLogicIsThinking: false,
      checkLogicIsProcessing: false,
      checkLogicHasThinking: false,

      // RhythmSound state
      rhythmSoundChangesMade: [],
      rhythmSoundStreamingThinking: null,
      rhythmSoundIsThinking: false,
      rhythmSoundIsProcessing: false,
      rhythmSoundHasThinking: false,

      // Rhythm standalone state
      rhythmChangesMade: [],
      rhythmStreamingThinking: null,
      rhythmIsThinking: false,
      rhythmIsProcessing: false,
      rhythmHasThinking: false,

      // Voice standalone state
      voiceChangesMade: [],
      voiceStreamingThinking: null,
      voiceIsThinking: false,
      voiceIsProcessing: false,
      voiceHasThinking: false,

      // Responder state
      responderResponse: null,
      responderFollowUpQuestion: null,
      responderStreamingThinking: null,
      responderIsThinking: false,
      responderIsProcessing: false,
      responderHasThinking: false,

      // Final diff state
      finalDiff: null,
      showFinalDiff: false,

      // Attachment refresh trigger
      attachmentVersion: 0,

      // Actions
      setZinsserMode: (mode) => set({ zinsserMode: mode }),
      setRunMode: (mode) => set({ runMode: mode }),
      setSessionName: (name) => set({ sessionName: name }),

      addUserTurn: (content) =>
        set((state) => ({
          turns: [
            ...state.turns,
            {
              role: 'user',
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      addAssistantTurn: (response) =>
        set((state) => ({
          sessionId: response.session_id,
          turns: [
            ...state.turns,
            {
              role: 'assistant',
              content: response.coaching_message,
              timestamp: new Date().toISOString(),
            },
          ],
          currentQueries: response.current_queries,
          narrativeDirection: response.narrative_direction,
          keyThemes: response.key_themes,
          phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
          workflowStatus: 'idle',
        })),

      setWorkflowStatus: (status) => set({ workflowStatus: status }),

      setCurrentTaskId: (taskId) => set({ currentTaskId: taskId }),

      setError: (error) => set({ error, workflowStatus: 'error' }),

      clearNoChangesMessage: () => set({ noChangesMessage: null }),

      startNewSession: () =>
        set({
          sessionId: null,
          sessionName: 'New Session',
          zinsserMode: 'gatekeeper',
          turns: [],
          currentQueries: [],
          narrativeDirection: '',
          keyThemes: [],
          phase: 'interviewing',
          workflowStatus: 'idle',
          currentTaskId: null,
          error: null,
          streamingContent: null,
          streamingThinking: null,
          isThinking: false,
          queryResults: [],
          totalCards: 0,
          isRetrieving: false,
          // Reset brainstorm state - always in brainstorm mode now
          brainstormMode: true,
          brainstormContent: '',
          brainstormCleanContent: '',
          brainstormDisplayContent: null,
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: '',
          brainstormKeyThemes: [],
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
          brainstormStreamingThinking: null,
          brainstormIsThinking: false,
          brainstormStreamingDocument: '',
          brainstormDocumentStreaming: false,
          brainstormDocumentPending: false,
          brainstormStreamingLineCount: 0,
          // Reset foolscap state
          foolscapContent: '',
          foolscapPhase: 'draft_creation',
          foolscapStreamingContent: null,
          foolscapStreamingThinking: null,
          foolscapIsThinking: false,
          foolscapDocumentPending: false,
          // Reset scratchpad state
          scratchpadContent: '',
          // Reset gatekeeper state
          gatekeeperTurns: [],
          gatekeeperStreamingContent: null,
          gatekeeperStreamingThinking: null,
          gatekeeperIsThinking: false,
          // Clear sessions list so stale data from other users isn't shown
          sessions: [],
          // Clear no changes message
          noChangesMessage: null,
          // Reset anatomy state
          anatomyStructure: null,
          anatomyStructureHash: '',
          anatomyStructureLoading: false,
          anatomyInsightCache: {},
          anatomyInsightLoading: false,
        }),

      loadSession: (sessionId, turns, queries, direction, themes) =>
        set({
          sessionId,
          turns,
          currentQueries: queries,
          narrativeDirection: direction,
          keyThemes: themes,
          phase: 'interviewing',
          workflowStatus: 'idle',
          error: null,
          noChangesMessage: null,
        }),

      // Session management actions
      openSessionPicker: () => set({ showSessionPicker: true }),
      closeSessionPicker: () => set({ showSessionPicker: false }),
      openSessionSidebar: () => set({ showSessionSidebar: true }),
      closeSessionSidebar: () => set({ showSessionSidebar: false }),
      toggleSessionSidebar: () => set((state) => ({ showSessionSidebar: !state.showSessionSidebar })),
      setMobileView: (view) => set({ mobileView: view }),

      fetchSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listZinsserSessions('active', 50, 0)
          set({ sessions, isLoadingSessions: false })
        } catch (error) {
          console.error('Failed to fetch sessions:', error)
          set({ isLoadingSessions: false })
        }
      },

      createNewSession: async (name?: string) => {
        try {
          const session = await createZinsserSession(name, 'gatekeeper')
          set({
            sessionId: session.session_id,
            sessionName: session.name,
            zinsserMode: session.mode as ZinsserMode,
            turns: [],
            currentQueries: [],
            narrativeDirection: '',
            keyThemes: [],
            phase: 'interviewing',
            workflowStatus: 'idle',
            brainstormMode: true,
            brainstormContent: session.new_doc || '',
            brainstormCleanContent: '',  // Reset for new session - prevents diff against old session
            brainstormDisplayContent: null,
            brainstormTurns: [],
            showSessionPicker: false,
            // Clear documents from previous session
            foolscapContent: '',
            foolscapCleanContent: '',  // Reset for new session - prevents diff against old session
            foolscapDisplayContent: null,
            foolscapPhase: 'draft_creation',
            scratchpadContent: '',
            scratchpadCleanContent: '',  // Reset for new session
            scratchpadDisplayContent: null,
            brainstormKeyThemes: [],
            brainstormGaps: [],
            gatekeeperTurns: [],
            gatekeeperStreamingContent: null,
            gatekeeperStreamingThinking: null,
            gatekeeperIsThinking: false,
            // Reset Zinsser state for new session
            zinsserDoc: '',
            zinsserDocAnnotated: '',
            showAnnotations: false,
            zinsserDiagnosis: null,
            zinsserChangesMade: [],
            zinsserStreamingThinking: null,
            zinsserIsThinking: false,
            zinsserIsProcessing: false,
            zinsserHasThinking: false,
            // Reset CutClutter state for new session
            cutClutterChangesMade: [],
            cutClutterStreamingThinking: null,
            cutClutterIsThinking: false,
            cutClutterIsProcessing: false,
            cutClutterHasThinking: false,
            // Reset StrengthenVerbs state for new session
            strengthenVerbsChangesMade: [],
            strengthenVerbsStreamingThinking: null,
            strengthenVerbsIsThinking: false,
            strengthenVerbsIsProcessing: false,
            strengthenVerbsHasThinking: false,
            // Reset CheckLogic state for new session
            checkLogicChangesMade: [],
            checkLogicStreamingThinking: null,
            checkLogicIsThinking: false,
            checkLogicIsProcessing: false,
            checkLogicHasThinking: false,
            // Reset RhythmSound state for new session
            rhythmSoundChangesMade: [],
            rhythmSoundStreamingThinking: null,
            rhythmSoundIsThinking: false,
            rhythmSoundIsProcessing: false,
            rhythmSoundHasThinking: false,
            // Reset Rhythm standalone state for new session
            rhythmChangesMade: [],
            rhythmStreamingThinking: null,
            rhythmIsThinking: false,
            rhythmIsProcessing: false,
            rhythmHasThinking: false,
            // Reset Voice standalone state for new session
            voiceChangesMade: [],
            voiceStreamingThinking: null,
            voiceIsThinking: false,
            voiceIsProcessing: false,
            voiceHasThinking: false,
            // Reset Responder state for new session
            responderResponse: null,
            responderFollowUpQuestion: null,
            responderStreamingThinking: null,
            responderIsThinking: false,
            responderIsProcessing: false,
            responderHasThinking: false,
            // Reset final diff state
            finalDiff: null,
            showFinalDiff: false,
            // Reset anatomy state for new session
            anatomyStructure: null,
            anatomyStructureHash: '',
            anatomyStructureLoading: false,
            anatomyInsightCache: {},
            anatomyInsightLoading: false,
          })
        } catch (error) {
          console.error('Failed to create session:', error)
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          const session = await getZinsserSessionWithTurns(sessionId)

          // Filter turns by mode - each mode has its own chat history
          // Note: 'responder' mode turns are coaching responses that go in the gatekeeper chat
          const brainstormDbTurns = session.turns.filter((t) => t.mode === 'brainstorm')
          const gatekeeperDbTurns = session.turns.filter((t) => t.mode === 'gatekeeper' || t.mode === 'responder')

          // Convert DB turns to store Turn format
          const brainstormTurns: Turn[] = brainstormDbTurns.map((t) => ({
            role: t.role,
            content: t.content,
            timestamp: t.timestamp,
            thinking: t.thinking || undefined,
          }))

          const gatekeeperTurns: Turn[] = gatekeeperDbTurns.map((t) => {
            // Map node-specific thinking based on turn mode
            // During streaming, thinking is saved to DB with the turn's mode
            // On reload, we need to restore it to the correct field
            const isResponderTurn = t.mode === 'responder'
            return {
              role: t.role,
              content: t.content,
              timestamp: t.timestamp,
              thinking: t.thinking || undefined,
              // For responder-mode turns, the DB thinking IS the responder thinking
              responderThinking: isResponderTurn ? (t.thinking || undefined) : undefined,
              responderRan: isResponderTurn,
              // Note: Zinsser/CutClutter thinking is NOT persisted to DB currently
              // It would require storing node_outputs JSON or separate columns
              // For now, these flags/thinking are only visible during the session
            }
          })

          // Consolidated 3-field schema:
          // - new_doc: current canvas content (what user sees/edits)
          // - old_doc: baseline for diff computation
          // - zin_doc_annotated: AI edits with <ins>/<del> for "Show AI Changes"
          const brainstormContent = session.new_doc || ''
          const zinsserDocAnnotated = session.zin_doc_annotated || ''

          // Extract themes/gaps from session_context if available
          const sessionContext = session.session_context || {}
          const keyThemes: string[] = Array.isArray(sessionContext.key_themes) ? sessionContext.key_themes : []
          const gaps: string[] = Array.isArray(sessionContext.gaps) ? sessionContext.gaps : []

          set({
            sessionId: session.session_id,
            sessionName: session.name,
            zinsserMode: session.mode as ZinsserMode,
            turns: [],
            brainstormTurns,
            gatekeeperTurns,
            // Consolidated 3-field schema: new_doc is the canvas, zin_doc_annotated is for Show AI Changes
            brainstormContent: brainstormContent,
            brainstormCleanContent: brainstormContent,  // Same as brainstormContent (no separate clean field)
            brainstormDisplayContent: null,
            brainstormMode: true,
            phase: 'interviewing',
            workflowStatus: 'idle',
            showSessionPicker: false,
            // Foolscap/Scratchpad not persisted separately in consolidated schema
            foolscapContent: '',
            foolscapCleanContent: '',
            foolscapDisplayContent: null,
            foolscapPhase: 'draft_creation',
            scratchpadContent: '',
            scratchpadCleanContent: '',
            scratchpadDisplayContent: null,
            // Reset themes/gaps from session_context
            brainstormKeyThemes: keyThemes,
            brainstormGaps: gaps,
            // Load Zinsser state from DB
            zinsserDoc: brainstormContent,  // Canvas content
            zinsserDocAnnotated: zinsserDocAnnotated,  // Annotated for "Show AI Changes"
            showAnnotations: false,
            zinsserDiagnosis: null,
            zinsserChangesMade: [],
            zinsserStreamingThinking: null,
            zinsserIsThinking: false,
            zinsserIsProcessing: false,
            zinsserHasThinking: false,
            // Reset CutClutter state when switching sessions
            cutClutterChangesMade: [],
            cutClutterStreamingThinking: null,
            cutClutterIsThinking: false,
            cutClutterIsProcessing: false,
            cutClutterHasThinking: false,
            // Reset StrengthenVerbs state when switching sessions
            strengthenVerbsChangesMade: [],
            strengthenVerbsStreamingThinking: null,
            strengthenVerbsIsThinking: false,
            strengthenVerbsIsProcessing: false,
            strengthenVerbsHasThinking: false,
            // Reset CheckLogic state when switching sessions
            checkLogicChangesMade: [],
            checkLogicStreamingThinking: null,
            checkLogicIsThinking: false,
            checkLogicIsProcessing: false,
            checkLogicHasThinking: false,
            // Reset RhythmSound state when switching sessions
            rhythmSoundChangesMade: [],
            rhythmSoundStreamingThinking: null,
            rhythmSoundIsThinking: false,
            rhythmSoundIsProcessing: false,
            rhythmSoundHasThinking: false,
            // Reset Rhythm standalone state when switching sessions
            rhythmChangesMade: [],
            rhythmStreamingThinking: null,
            rhythmIsThinking: false,
            rhythmIsProcessing: false,
            rhythmHasThinking: false,
            // Reset Voice standalone state when switching sessions
            voiceChangesMade: [],
            voiceStreamingThinking: null,
            voiceIsThinking: false,
            voiceIsProcessing: false,
            voiceHasThinking: false,
            // Reset Responder state when switching sessions
            responderResponse: null,
            responderFollowUpQuestion: null,
            responderStreamingThinking: null,
            responderIsThinking: false,
            responderIsProcessing: false,
            responderHasThinking: false,
            // Reset final diff state
            finalDiff: null,
            showFinalDiff: false,
            // Reset anatomy state when switching sessions
            anatomyStructure: null,
            anatomyStructureHash: '',
            anatomyStructureLoading: false,
            anatomyInsightCache: {},
            anatomyInsightLoading: false,
          })
        } catch (error) {
          console.error('Failed to load session:', error)

          // CRITICAL: If session not found (404), it likely belongs to a different user
          // Clear the stale data and create a fresh session
          const errorMessage = error instanceof Error ? error.message : String(error)
          if (errorMessage.includes('Not Found') || errorMessage.includes('404')) {
            console.log('[zinsser-store] Session not found in DB, clearing stale data and creating new session')

            // Clear localStorage to remove the stale session ID
            localStorage.removeItem('zinsser-store-v3')

            // Reset to clean state
            useZinsserStore.getState().startNewSession()

            // Create a new session in the DB
            await useZinsserStore.getState().createNewSession()
          }
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await apiDeleteZSession(sessionId)
          set((state) => ({
            sessions: state.sessions.filter((s) => s.session_id !== sessionId),
          }))
        } catch (error) {
          console.error('Failed to delete session:', error)
        }
      },

      renameSession: async (name: string) => {
        const { sessionId } = useZinsserStore.getState()
        if (!sessionId) {
          // No session in DB yet, just update local state
          set({ sessionName: name })
          return
        }
        try {
          await updateZinsserSession(sessionId, { name })
          set({ sessionName: name })
        } catch (error) {
          console.error('Failed to rename session:', error)
        }
      },

      // Refresh session name from API if it may have been auto-generated
      // Called after streaming completes to pick up backend auto-naming
      refreshSessionNameIfNeeded: async () => {
        const { sessionId, sessionName, brainstormTurns } = useZinsserStore.getState()
        const AUTO_NAME_TURN_THRESHOLD = 3
        const DEFAULT_NAMES = ['New Session', 'Untitled Session', '']

        // Only refresh if we've hit the threshold and name is still default
        if (
          sessionId &&
          brainstormTurns.length >= AUTO_NAME_TURN_THRESHOLD &&
          DEFAULT_NAMES.includes(sessionName)
        ) {
          try {
            const session = await getZinsserSession(sessionId)
            if (session.name && !DEFAULT_NAMES.includes(session.name)) {
              set({ sessionName: session.name })
              console.log('[zinsser-store] Auto-updated session name:', session.name)
            }
          } catch (error) {
            console.error('Failed to refresh session name:', error)
          }
        }
      },

      // Streaming actions
      startStreaming: () =>
        set({
          workflowStatus: 'streaming',
          streamingContent: null,
          streamingThinking: null,
          isThinking: false,
        }),

      startThinking: () =>
        set({
          isThinking: true,
          streamingThinking: '',
        }),

      updateStreamingThinking: (delta) =>
        set((state) => ({
          streamingThinking: (state.streamingThinking || '') + delta,
        })),

      finishThinking: () =>
        set({
          isThinking: false,
        }),

      updateStreamingContent: (chunk) =>
        set((state) => {
          const current = state.streamingContent || {}
          // Accumulate coaching_message deltas for character-by-character streaming
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            streamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // Convert streaming message to real turn, unlock input while JSON finalizes
      commitStreamingMessage: () =>
        set((state) => {
          const content = state.streamingContent?.coaching_message || ''
          if (!content) return state
          return {
            turns: [
              ...state.turns,
              {
                role: 'assistant' as const,
                content,
                timestamp: new Date().toISOString(),
                thinking: state.streamingThinking || undefined,
              },
            ],
            workflowStatus: 'idle',
            streamingContent: null,
            streamingThinking: null,
            isThinking: false,
          }
        }),

      finishStreaming: (response) =>
        set((state) => {
          // Check if message was already committed (streamingContent is null)
          const alreadyCommitted = !state.streamingContent

          // If already committed, just update metadata without adding duplicate turn
          if (alreadyCommitted) {
            return {
              sessionId: response.session_id,
              currentQueries: response.current_queries,
              narrativeDirection: response.narrative_direction,
              keyThemes: response.key_themes,
              phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
              workflowStatus: 'idle',
              streamingThinking: null,
              isThinking: false,
            }
          }

          // Otherwise, add the turn and update everything
          return {
            sessionId: response.session_id,
            turns: [
              ...state.turns,
              {
                role: 'assistant' as const,
                content: response.coaching_message,
                timestamp: new Date().toISOString(),
                thinking: response.thinking || state.streamingThinking || undefined,
              },
            ],
            currentQueries: response.current_queries,
            narrativeDirection: response.narrative_direction,
            keyThemes: response.key_themes,
            phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
            workflowStatus: 'idle',
            streamingContent: null,
            streamingThinking: null,
            isThinking: false,
          }
        }),

      // Retrieval actions
      setIsRetrieving: (isRetrieving) => set({ isRetrieving }),

      setQueryResults: (results, total) =>
        set({
          queryResults: results,
          totalCards: total,
          isRetrieving: false,
        }),

      clearQueryResults: () =>
        set({
          queryResults: [],
          totalCards: 0,
        }),

      // Brainstorm actions
      enterBrainstormMode: () =>
        set((state) => ({
          brainstormMode: true,
          brainstormContent: '',
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: state.narrativeDirection,
          brainstormKeyThemes: state.keyThemes,
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
        })),

      exitBrainstormMode: () =>
        set({
          brainstormMode: false,
          brainstormContent: '',
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: '',
          brainstormKeyThemes: [],
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
        }),

      updateBrainstormContent: (content) =>
        set({
          brainstormContent: content,
          // Also update clean content baseline for next turn's diff computation
          // Strip any existing annotations to get clean version
          brainstormCleanContent: stripAnnotations(content),
        }),

      addBrainstormTurn: (role, content) =>
        set((state) => ({
          brainstormTurns: [
            ...state.brainstormTurns,
            {
              role,
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      setBrainstormPhase: (phase) =>
        set({ brainstormPhase: phase }),

      setBrainstormWorkingDirection: (direction) =>
        set({ brainstormWorkingDirection: direction }),

      setBrainstormKeyThemes: (themes) =>
        set({ brainstormKeyThemes: themes }),

      setBrainstormGaps: (gaps) =>
        set({ brainstormGaps: gaps }),

      addBrainstormUserTurn: (content) =>
        set((state) => ({
          brainstormTurns: [
            ...state.brainstormTurns,
            {
              role: 'user' as const,
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      // Brainstorm streaming actions
      startBrainstormStreaming: () =>
        set({
          workflowStatus: 'streaming',
          brainstormStreamingContent: { coaching_message: '' },  // Initialize with empty object to track streaming state
          brainstormStreamingThinking: null,
          brainstormIsThinking: false,
        }),

      updateBrainstormStreamingContent: (chunk) =>
        set((state) => {
          const current = state.brainstormStreamingContent || { coaching_message: '' }
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            brainstormStreamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // SET (not append) coaching message - for complete messages from backend
      setBrainstormCoachingMessage: (message) =>
        set((state) => ({
          brainstormStreamingContent: {
            ...state.brainstormStreamingContent,
            coaching_message: message,
          },
        })),

      startBrainstormThinking: () =>
        set({
          brainstormIsThinking: true,
          brainstormStreamingThinking: '',
        }),

      updateBrainstormThinking: (delta) =>
        set((state) => ({
          brainstormStreamingThinking: (state.brainstormStreamingThinking || '') + delta,
        })),

      finishBrainstormThinking: () =>
        set({
          brainstormIsThinking: false,
        }),

      commitBrainstormStreamingMessage: () =>
        set((state) => {
          const content = state.brainstormStreamingContent?.coaching_message || ''
          if (!content) return state
          return {
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content,
                thinking: state.brainstormStreamingThinking || undefined,
                timestamp: new Date().toISOString(),
              },
            ],
            workflowStatus: 'idle',
            brainstormStreamingContent: null,
            brainstormStreamingThinking: null,
            brainstormIsThinking: false,
            // Document update is still pending
            brainstormDocumentPending: true,
          }
        }),

      finishBrainstormStreaming: (output) =>
        set((state) => {
          const alreadyCommitted = !state.brainstormStreamingContent
          const sessionContext = output.session_context || {}

          // NEW JSON OUTPUT FORMAT: AI returns new_doc with annotations embedded
          // No need to compute diffs - just use the content directly
          let contentToUse: string
          if (output.brainstorm_modified && output.new_doc) {
            // AI modified the document - use annotated content directly
            contentToUse = output.new_doc
          } else if (state.brainstormContent && state.brainstormContent.length > 0) {
            // No modification - keep existing content
            contentToUse = state.brainstormContent
          } else {
            // Fallback to backend content
            contentToUse = output.new_doc || ''
          }

          // Scratchpad content - also from JSON output
          let scratchpadToUse: string
          if (output.scratchpad_modified && output.scratchpad_content) {
            scratchpadToUse = output.scratchpad_content
          } else {
            scratchpadToUse = output.scratchpad_content !== undefined
              ? output.scratchpad_content
              : state.scratchpadContent
          }

          console.log('[zinsser-store] finishBrainstormStreaming (JSON output)')
          console.log('[zinsser-store] output.brainstorm_modified:', output.brainstorm_modified)
          console.log('[zinsser-store] contentToUse length:', contentToUse.length)
          console.log('[zinsser-store] Has annotations:', contentToUse.includes('==') || contentToUse.includes('~~'))

          const updates: Partial<ZState> = {
            // Store annotated content for display
            brainstormContent: contentToUse,
            // Clear transient display content
            brainstormDisplayContent: null,
            // Store clean content for next turn's diff computation (from backend)
            brainstormCleanContent: output.old_doc || stripAnnotations(contentToUse),
            brainstormPhase: sessionContext.phase || state.brainstormPhase,
            // Map new session_context fields
            brainstormWorkingDirection: sessionContext.context_summary || state.brainstormWorkingDirection,
            brainstormKeyThemes: sessionContext.key_insights || state.brainstormKeyThemes,
            brainstormGaps: state.brainstormGaps,  // No longer tracked in new format
            workflowStatus: 'idle',
            brainstormStreamingContent: null,
            brainstormDocumentPending: false,
            brainstormDocumentStreaming: false,
            // Scratchpad content
            scratchpadContent: scratchpadToUse,
            scratchpadCleanContent: output.new_scratchpad_content || stripAnnotations(scratchpadToUse),
            scratchpadDisplayContent: null,
            // Clear scratchpad streaming state
            scratchpadDocumentStreaming: false,
            scratchpadStreamingDocument: '',
            scratchpadStreamingLineCount: 0,
            // Session name from AI suggestion
            ...(output.suggested_session_name ? { sessionName: output.suggested_session_name } : {}),
            // Update sessions list with new name (for sidebar display)
            ...(output.suggested_session_name ? {
              sessions: state.sessions.map(s =>
                s.session_id === state.sessionId
                  ? { ...s, name: output.suggested_session_name! }
                  : s
              )
            } : {}),
          }

          if (alreadyCommitted) {
            return updates
          }

          return {
            ...updates,
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content: output.coaching_message,
                timestamp: new Date().toISOString(),
                // Prefer thinking from output (complete), fall back to streaming thinking
                thinking: output.thinking || state.brainstormStreamingThinking || undefined,
              },
            ],
            // Clear streaming thinking now that it's committed to turn
            brainstormStreamingThinking: null,
          }
        }),

      // Document streaming actions - line-by-line with frontend diff computation
      // Key insight: Keep showing the OLD document during streaming, only show diff at the END
      startBrainstormDocumentStreaming: () => {
        console.log('[zinsser-store] startBrainstormDocumentStreaming')
        set({
          brainstormDocumentStreaming: true,
          brainstormDocumentPending: true,
          brainstormStreamingDocument: '',
          brainstormStreamingLineCount: 0,
          // DON'T clear brainstormContent - keep showing existing document
        })
      },

      appendBrainstormDocumentLine: (line) => {
        const state = useZinsserStore.getState()
        const newStreamingDoc = state.brainstormStreamingDocument + line
        const newLineCount = state.brainstormStreamingLineCount + 1

        console.log('[zinsser-store] appendBrainstormDocumentLine, line:', newLineCount, 'total length:', newStreamingDoc.length)

        // Compute diff of accumulated content vs old content - update display on EVERY line
        // Pass isComplete=false to avoid marking remaining old lines as deleted prematurely
        const annotatedContent = computeStreamingDiff(
          state.brainstormCleanContent,  // Old document (clean, no annotations)
          newStreamingDoc,               // New document being streamed
          false                          // isComplete=false: don't mark remaining old lines as deleted
        )

        set({
          brainstormStreamingDocument: newStreamingDoc,
          brainstormDisplayContent: annotatedContent,  // Update display in real-time
          brainstormStreamingLineCount: newLineCount,  // Track for progress indicator
        })
      },

      finishBrainstormDocumentStreaming: (fullDocument) => {
        const state = useZinsserStore.getState()
        console.log('[zinsser-store] finishBrainstormDocumentStreaming, doc length:', fullDocument.length)
        console.log('[zinsser-store] cleanContent length:', state.brainstormCleanContent.length)

        // Streaming complete - now compute full diff WITH deletions marked
        const annotatedContent = computeStreamingDiff(
          state.brainstormCleanContent,
          fullDocument,
          true  // isComplete=true: mark remaining old lines as deleted
        )
        console.log('[zinsser-store] annotatedContent length:', annotatedContent.length)

        // Use NON-PERSISTED brainstormDisplayContent to avoid localStorage race conditions
        // This field is not in partialize(), so persist middleware ignores it entirely
        set({
          brainstormDisplayContent: annotatedContent,  // Transient display - NOT persisted
          brainstormCleanContent: fullDocument,
          brainstormStreamingDocument: '',
          brainstormDocumentStreaming: false,
          brainstormDocumentPending: false,
          brainstormStreamingLineCount: 0,  // Reset line count
        })
        console.log('[zinsser-store] Set brainstormDisplayContent (transient), length:', annotatedContent.length)
      },

      // Legacy: Instant document update - receives complete annotated document from backend
      updateBrainstormDocumentInstant: (content) =>
        set({
          brainstormContent: content,
          brainstormDocumentPending: false,
        }),

      // Foolscap actions
      updateFoolscapContent: (content) =>
        set({
          foolscapContent: content,
          // Also update clean content baseline for next turn's diff computation
          foolscapCleanContent: stripAnnotations(content),
        }),

      startFoolscapStreaming: () =>
        set({
          workflowStatus: 'streaming',
          foolscapStreamingContent: { coaching_message: '' },  // Initialize with empty object to track streaming state
          foolscapStreamingThinking: null,
          foolscapIsThinking: false,
          // Show pending overlay immediately when starting foolscap streaming
          foolscapDocumentPending: true,
        }),

      updateFoolscapStreamingContent: (chunk) =>
        set((state) => {
          const current = state.foolscapStreamingContent || { coaching_message: '' }
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            foolscapStreamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // SET (not append) coaching message - for complete messages that arrive at once
      setFoolscapCoachingMessage: (message) =>
        set((state) => ({
          foolscapStreamingContent: {
            ...state.foolscapStreamingContent,
            coaching_message: message,
          },
        })),

      startFoolscapThinking: () =>
        set({
          foolscapIsThinking: true,
          foolscapStreamingThinking: '',
        }),

      updateFoolscapThinking: (delta) =>
        set((state) => ({
          foolscapStreamingThinking: (state.foolscapStreamingThinking || '') + delta,
        })),

      finishFoolscapThinking: () =>
        set({
          foolscapIsThinking: false,
        }),

      commitFoolscapStreamingMessage: () =>
        set((state) => {
          const content = state.foolscapStreamingContent?.coaching_message || ''
          if (!content) return state
          return {
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content,
                thinking: state.foolscapStreamingThinking || undefined,
                timestamp: new Date().toISOString(),
              },
            ],
            workflowStatus: 'idle',
            foolscapStreamingContent: null,
            foolscapStreamingThinking: null,
            foolscapIsThinking: false,
            foolscapDocumentPending: true,
          }
        }),

      finishFoolscapStreaming: (output) =>
        set((state) => {
          const alreadyCommitted = !state.foolscapStreamingContent
          const sessionContext = output.session_context || {}

          // Determine content to display with annotations
          // Priority:
          // 1. displayContent (from streaming diff) - already has annotations
          // 2. Compute diff if we have new content from backend but displayContent is null
          // 3. Keep existing content if no document change this turn
          let contentToUse: string
          if (state.foolscapDisplayContent !== null) {
            // Streaming set displayContent - use it (has annotations)
            contentToUse = state.foolscapDisplayContent
          } else if (output.new_foolscap_content && output.foolscap_modified !== false) {
            // Backend sent new content but displayContent wasn't set (fallback)
            // Compute diff now to ensure annotations are shown
            contentToUse = computeStreamingDiff(
              state.foolscapCleanContent,
              output.new_foolscap_content,
              true  // isComplete
            )
          } else if (output.foolscap_content !== undefined) {
            contentToUse = output.foolscap_content
          } else {
            contentToUse = state.foolscapContent
          }

          // Determine final content to use for scratchpad
          // Same pattern: prefer displayContent, compute diff as fallback
          let scratchpadToUse: string
          if (state.scratchpadDisplayContent !== null) {
            scratchpadToUse = state.scratchpadDisplayContent
          } else if (output.new_scratchpad_content && output.scratchpad_modified !== false) {
            // Compute diff for scratchpad
            scratchpadToUse = computeStreamingDiff(
              state.scratchpadCleanContent,
              output.new_scratchpad_content,
              true
            )
          } else {
            scratchpadToUse = output.scratchpad_content !== undefined
              ? output.scratchpad_content
              : state.scratchpadContent
          }

          const updates: Partial<ZState> = {
            foolscapContent: contentToUse,
            // Save clean content for next turn diff computation
            foolscapCleanContent: output.new_foolscap_content || stripAnnotations(contentToUse),
            // Clear transient display content now that we've committed
            foolscapDisplayContent: null,
            foolscapPhase: sessionContext.phase || state.foolscapPhase,
            // Scratchpad content
            scratchpadContent: scratchpadToUse,
            scratchpadCleanContent: output.new_scratchpad_content || stripAnnotations(scratchpadToUse),
            scratchpadDisplayContent: null,
            workflowStatus: 'idle',
            foolscapStreamingContent: null,
            foolscapDocumentPending: false,
            // Clear document streaming state
            foolscapDocumentStreaming: false,
            foolscapStreamingDocument: '',
            foolscapStreamingLineCount: 0,
            // Clear scratchpad streaming state
            scratchpadDocumentStreaming: false,
            scratchpadStreamingDocument: '',
            scratchpadStreamingLineCount: 0,
          }

          // If finalized brainstorm content is provided (first foolscap turn),
          // update brainstormContent with the clean version (annotations stripped)
          if (output.finalized_brainstorm_content) {
            updates.brainstormContent = output.finalized_brainstorm_content
          }

          if (alreadyCommitted) {
            return updates
          }

          const messageContent = output.question
            ? `${output.coaching_message}\n\n${output.question}`
            : output.coaching_message

          return {
            ...updates,
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content: messageContent,
                timestamp: new Date().toISOString(),
                // Prefer thinking from output (complete), fall back to streaming thinking
                thinking: output.thinking || state.foolscapStreamingThinking || undefined,
              },
            ],
            // Clear streaming thinking now that it's committed to turn
            foolscapStreamingThinking: null,
          }
        }),

      // Document streaming - line-by-line streaming with frontend diff computation
      startFoolscapDocumentStreaming: () =>
        set({
          foolscapStreamingDocument: '',
          foolscapDocumentStreaming: true,
          foolscapDocumentPending: false,  // Not pending - actively streaming
          foolscapStreamingLineCount: 0,
        }),

      appendFoolscapDocumentLine: (line) =>
        set((state) => {
          const newDocument = state.foolscapStreamingDocument + line
          const lineCount = state.foolscapStreamingLineCount + 1

          // Compute progressive diff as lines stream in
          const annotatedContent = computeStreamingDiff(
            state.foolscapCleanContent,
            newDocument,
            false  // Not complete yet - don't mark remaining old lines as deleted
          )

          return {
            foolscapStreamingDocument: newDocument,
            foolscapStreamingLineCount: lineCount,
            // Update display content with annotated diff (transient, not persisted)
            foolscapDisplayContent: annotatedContent,
          }
        }),

      finishFoolscapDocumentStreaming: (fullDocument) =>
        set((state) => {
          // Compute final diff with all deletions marked
          const annotatedContent = computeStreamingDiff(
            state.foolscapCleanContent,
            fullDocument,
            true  // Complete - mark all unmatched old lines as deleted
          )

          return {
            foolscapDocumentStreaming: false,
            foolscapStreamingDocument: '',
            foolscapStreamingLineCount: 0,
            // Set display content to final annotated version
            foolscapDisplayContent: annotatedContent,
            // Update clean content for next turn's diff computation
            foolscapCleanContent: fullDocument,
          }
        }),

      // Legacy: Document update - receives complete annotated document from backend
      updateFoolscapDocumentInstant: (content) =>
        set({
          foolscapContent: content,
          foolscapDocumentPending: false,
        }),

      // Scratchpad actions
      updateScratchpadContent: (content) =>
        set({
          scratchpadContent: content,
          // Also update clean content baseline for next turn's diff computation
          scratchpadCleanContent: stripAnnotations(content),
        }),

      // Scratchpad streaming - line-by-line streaming with frontend diff computation
      startScratchpadDocumentStreaming: () =>
        set({
          scratchpadStreamingDocument: '',
          scratchpadDocumentStreaming: true,
          scratchpadStreamingLineCount: 0,
        }),

      appendScratchpadDocumentLine: (line) =>
        set((state) => {
          const newDocument = state.scratchpadStreamingDocument + line
          const lineCount = state.scratchpadStreamingLineCount + 1

          // Compute progressive diff as lines stream in
          const annotatedContent = computeStreamingDiff(
            state.scratchpadCleanContent,
            newDocument,
            false  // Not complete yet - don't mark remaining old lines as deleted
          )

          return {
            scratchpadStreamingDocument: newDocument,
            scratchpadStreamingLineCount: lineCount,
            // Update display content with annotated diff (transient, not persisted)
            scratchpadDisplayContent: annotatedContent,
          }
        }),

      finishScratchpadDocumentStreaming: (fullScratchpad) =>
        set((state) => {
          // Compute final diff with complete flag
          const annotatedContent = computeStreamingDiff(
            state.scratchpadCleanContent,
            fullScratchpad,
            true  // Complete - mark all unmatched old lines as deleted
          )

          return {
            scratchpadDocumentStreaming: false,
            scratchpadStreamingDocument: '',
            scratchpadStreamingLineCount: 0,
            // Set display content to final annotated version
            scratchpadDisplayContent: annotatedContent,
            // Update clean content for next turn's diff computation
            scratchpadCleanContent: fullScratchpad,
          }
        }),

      // Gatekeeper actions
      addGatekeeperUserTurn: (content) =>
        set((state) => ({
          gatekeeperTurns: [
            ...state.gatekeeperTurns,
            {
              role: 'user' as const,
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      startGatekeeperStreaming: () =>
        set({
          workflowStatus: 'streaming',
          gatekeeperStreamingContent: { response: '' },
          gatekeeperStreamingThinking: null,
          gatekeeperIsThinking: false,
          // Clear annotated doc from previous turn - will be set again if editing runs this turn
          // This prevents Edits tab from showing stale annotations when only gatekeeper runs
          zinsserDocAnnotated: '',
          // Reset all node state for the new turn
          // Previous turn's thinking is already persisted in the Turn object
          zinsserIsProcessing: false,
          zinsserIsThinking: false,
          zinsserHasThinking: false,
          zinsserStreamingThinking: null,
          cutClutterIsProcessing: false,
          cutClutterIsThinking: false,
          cutClutterHasThinking: false,
          cutClutterStreamingThinking: null,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsIsThinking: false,
          strengthenVerbsHasThinking: false,
          strengthenVerbsStreamingThinking: null,
          checkLogicIsProcessing: false,
          checkLogicIsThinking: false,
          checkLogicHasThinking: false,
          checkLogicStreamingThinking: null,
          rhythmSoundIsProcessing: false,
          rhythmSoundIsThinking: false,
          rhythmSoundHasThinking: false,
          rhythmSoundStreamingThinking: null,
          responderIsProcessing: false,
          responderIsThinking: false,
          responderHasThinking: false,
          responderStreamingThinking: null,
          responderResponse: null,
          responderFollowUpQuestion: null,
        }),

      startGatekeeperThinking: () =>
        set({
          gatekeeperIsThinking: true,
          gatekeeperStreamingThinking: '',
        }),

      updateGatekeeperThinking: (delta) =>
        set((state) => ({
          gatekeeperStreamingThinking: (state.gatekeeperStreamingThinking || '') + delta,
        })),

      finishGatekeeperThinking: () =>
        set({
          gatekeeperIsThinking: false,
        }),

      finishGatekeeperStreaming: (output) =>
        set((state) => ({
          gatekeeperTurns: [
            ...state.gatekeeperTurns,
            {
              role: 'assistant' as const,
              content: output.response,
              thinking: state.gatekeeperStreamingThinking || undefined,
              timestamp: new Date().toISOString(),
            },
          ],
          workflowStatus: 'idle',
          gatekeeperStreamingContent: null,
          gatekeeperStreamingThinking: null,
          gatekeeperIsThinking: false,
        })),

      // === Zinsser actions ===
      startZinsserProcessing: () =>
        set({
          zinsserIsProcessing: true,
          zinsserHasThinking: true,  // Mark that Zinsser ran this turn
          zinsserStreamingThinking: '',
          workflowStatus: 'processing',
        }),

      startZinsserThinking: () =>
        set({
          zinsserIsThinking: true,
          zinsserStreamingThinking: '',
        }),

      updateZinsserThinking: (delta) =>
        set((state) => ({
          zinsserStreamingThinking: (state.zinsserStreamingThinking || '') + delta,
        })),

      finishZinsserThinking: () =>
        set({
          zinsserIsThinking: false,
        }),

      finishZinsserProcessing: (output) =>
        set({
          zinsserDoc: output.zin_doc,
          zinsserDiagnosis: output.diagnosis,
          zinsserChangesMade: output.changes_made,
          zinsserIsProcessing: false,
          zinsserIsThinking: false,
          // Don't set workflowStatus to idle - CutClutter will run next
          // Update canvas with macro-edited document so user sees the changes
          brainstormContent: output.zin_doc,
          brainstormCleanContent: output.zin_doc,
        }),

      // === CutClutter actions (micro editing - Pass 1) ===
      // For standalone workflows (via context menu), clear other micro-editing state
      startCutClutterProcessing: () =>
        set({
          cutClutterIsProcessing: true,
          cutClutterHasThinking: true,
          cutClutterStreamingThinking: '',
          workflowStatus: 'processing',
          isStandaloneWorkflow: true,  // Mark as standalone (no Gatekeeper)
          // Clear other micro-editing state (for standalone workflow isolation)
          strengthenVerbsHasThinking: false,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsStreamingThinking: null,
          checkLogicHasThinking: false,
          checkLogicIsProcessing: false,
          checkLogicStreamingThinking: null,
          rhythmHasThinking: false,
          rhythmIsProcessing: false,
          rhythmStreamingThinking: null,
          voiceHasThinking: false,
          voiceIsProcessing: false,
          voiceStreamingThinking: null,
        }),

      startCutClutterThinking: () =>
        set({
          cutClutterIsThinking: true,
          cutClutterStreamingThinking: '',
        }),

      updateCutClutterThinking: (delta) =>
        set((state) => ({
          cutClutterStreamingThinking: (state.cutClutterStreamingThinking || '') + delta,
        })),

      finishCutClutterThinking: () =>
        set({
          cutClutterIsThinking: false,
        }),

      finishCutClutterProcessing: (output) =>
        set({
          cutClutterChangesMade: output.changes_made,
          cutClutterIsProcessing: false,
          cutClutterIsThinking: false,
          // Don't set workflowStatus to idle - StrengthenVerbs will run next
          // Update canvas with clutter-cut document
          brainstormContent: output.zin_doc,
          brainstormCleanContent: output.zin_doc,
        }),

      // Standalone Cut Clutter workflow (right-click context menu - bypasses chat)
      // Creates an assistant turn with the edit results, similar to a full workflow run
      finishStandaloneCutClutter: (output) =>
        set((state) => {
          // Check if any changes were made
          const hasChanges = output.final_diff?.has_changes ?? false

          // Guard: Check if we already added a Cut Clutter turn recently (prevent duplicates)
          const lastTurn = state.gatekeeperTurns[state.gatekeeperTurns.length - 1]
          const isDuplicateCall = lastTurn?.cutClutterRan === true &&
            lastTurn?.content === 'Applied Zinsser clutter removal to selection.'

          if (isDuplicateCall) {
            console.log('[finishStandaloneCutClutter] Skipping duplicate turn creation')
            return {
              // Still update the document and clear state, but don't add another turn
              brainstormContent: output.new_doc,
              brainstormCleanContent: stripAnnotations(output.new_doc),
              zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
              noChangesMessage: hasChanges ? null : 'No clutter found in selection',
              cutClutterIsProcessing: false,
              cutClutterIsThinking: false,
              cutClutterStreamingThinking: '',
              workflowStatus: 'idle',
              isStandaloneWorkflow: false,
            }
          }

          // Create a turn representing this Cut Clutter action
          // No diff viewer - changes are already visible in the Edit tab
          const cutClutterTurn: Turn = {
            role: 'assistant',
            content: hasChanges ? 'Applied Zinsser clutter removal to selection.' : 'No clutter found in selection.',
            timestamp: new Date().toISOString(),
            cutClutterRan: true,
            cutClutterThinking: state.cutClutterStreamingThinking || undefined,
          }

          return {
            gatekeeperTurns: [...state.gatekeeperTurns, cutClutterTurn],
            brainstormContent: output.new_doc,
            brainstormCleanContent: stripAnnotations(output.new_doc),
            zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
            noChangesMessage: hasChanges ? null : 'No clutter found in selection',
            // Clear Cut Clutter streaming state
            cutClutterIsProcessing: false,
            cutClutterIsThinking: false,
            cutClutterStreamingThinking: '',
            workflowStatus: 'idle',
            isStandaloneWorkflow: false,
          }
        }),

      // Standalone Strengthen Verbs workflow (right-click context menu - bypasses chat)
      // Creates an assistant turn with the edit results, similar to a full workflow run
      finishStandaloneStrengthenVerbs: (output) =>
        set((state) => {
          // Check if any changes were made
          const hasChanges = output.final_diff?.has_changes ?? false

          // Guard: Check if we already added a Strengthen Verbs turn recently (prevent duplicates)
          const lastTurn = state.gatekeeperTurns[state.gatekeeperTurns.length - 1]
          const isDuplicateCall = lastTurn?.strengthenVerbsRan === true &&
            lastTurn?.content === 'Applied Zinsser verb strengthening to selection.'

          if (isDuplicateCall) {
            console.log('[finishStandaloneStrengthenVerbs] Skipping duplicate turn creation')
            return {
              // Still update the document and clear state, but don't add another turn
              brainstormContent: output.new_doc,
              brainstormCleanContent: stripAnnotations(output.new_doc),
              zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
              noChangesMessage: hasChanges ? null : 'No weak verbs found in selection',
              strengthenVerbsIsProcessing: false,
              strengthenVerbsIsThinking: false,
              strengthenVerbsStreamingThinking: '',
              workflowStatus: 'idle',
              isStandaloneWorkflow: false,
            }
          }

          // Create a turn representing this Strengthen Verbs action
          // No diff viewer - changes are already visible in the Edit tab
          const strengthenVerbsTurn: Turn = {
            role: 'assistant',
            content: hasChanges ? 'Applied Zinsser verb strengthening to selection.' : 'No weak verbs found in selection.',
            timestamp: new Date().toISOString(),
            strengthenVerbsRan: true,
            strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
          }

          return {
            gatekeeperTurns: [...state.gatekeeperTurns, strengthenVerbsTurn],
            brainstormContent: output.new_doc,
            brainstormCleanContent: stripAnnotations(output.new_doc),
            zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
            noChangesMessage: hasChanges ? null : 'No weak verbs found in selection',
            // Clear Strengthen Verbs streaming state
            strengthenVerbsIsProcessing: false,
            strengthenVerbsIsThinking: false,
            strengthenVerbsStreamingThinking: '',
            workflowStatus: 'idle',
            isStandaloneWorkflow: false,
          }
        }),

      // === StrengthenVerbs actions (micro editing - Pass 2) ===
      // For standalone workflows (via context menu), clear other micro-editing state
      startStrengthenVerbsProcessing: () =>
        set({
          strengthenVerbsIsProcessing: true,
          strengthenVerbsHasThinking: true,
          strengthenVerbsStreamingThinking: '',
          workflowStatus: 'processing',
          isStandaloneWorkflow: true,  // Mark as standalone (no Gatekeeper)
          // Clear other micro-editing state (for standalone workflow isolation)
          cutClutterHasThinking: false,
          cutClutterIsProcessing: false,
          cutClutterStreamingThinking: null,
          checkLogicHasThinking: false,
          checkLogicIsProcessing: false,
          checkLogicStreamingThinking: null,
          rhythmHasThinking: false,
          rhythmIsProcessing: false,
          rhythmStreamingThinking: null,
          voiceHasThinking: false,
          voiceIsProcessing: false,
          voiceStreamingThinking: null,
        }),

      startStrengthenVerbsThinking: () =>
        set({
          strengthenVerbsIsThinking: true,
          strengthenVerbsStreamingThinking: '',
        }),

      updateStrengthenVerbsThinking: (delta) =>
        set((state) => ({
          strengthenVerbsStreamingThinking: (state.strengthenVerbsStreamingThinking || '') + delta,
        })),

      finishStrengthenVerbsThinking: () =>
        set({
          strengthenVerbsIsThinking: false,
        }),

      finishStrengthenVerbsProcessing: (output) =>
        set({
          strengthenVerbsChangesMade: output.changes_made,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsIsThinking: false,
          // Don't set workflowStatus to idle - CheckLogic will run next
          // Update canvas with verb-strengthened document
          brainstormContent: output.zin_doc,
          brainstormCleanContent: output.zin_doc,
        }),

      // === Rhythm standalone workflow actions (right-click context menu) ===
      // Clear other micro-editing state for standalone workflow isolation
      startRhythmProcessing: () =>
        set({
          rhythmIsProcessing: true,
          rhythmHasThinking: true,
          rhythmStreamingThinking: '',
          workflowStatus: 'processing',
          isStandaloneWorkflow: true,  // Mark as standalone (no Gatekeeper)
          // Clear other micro-editing state (for standalone workflow isolation)
          cutClutterHasThinking: false,
          cutClutterIsProcessing: false,
          cutClutterStreamingThinking: null,
          strengthenVerbsHasThinking: false,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsStreamingThinking: null,
          checkLogicHasThinking: false,
          checkLogicIsProcessing: false,
          checkLogicStreamingThinking: null,
          voiceHasThinking: false,
          voiceIsProcessing: false,
          voiceStreamingThinking: null,
        }),

      startRhythmThinking: () =>
        set({
          rhythmIsThinking: true,
          rhythmStreamingThinking: '',
        }),

      updateRhythmThinking: (delta) =>
        set((state) => ({
          rhythmStreamingThinking: (state.rhythmStreamingThinking || '') + delta,
        })),

      finishRhythmThinking: () =>
        set({
          rhythmIsThinking: false,
        }),

      // Standalone Rhythm workflow (right-click context menu - bypasses chat)
      // Creates an assistant turn with the edit results, similar to other standalone workflows
      finishStandaloneRhythm: (output) =>
        set((state) => {
          // Check if any changes were made
          const hasChanges = output.final_diff?.has_changes ?? false

          // Guard: Check if we already added a Rhythm turn recently (prevent duplicates)
          const lastTurn = state.gatekeeperTurns[state.gatekeeperTurns.length - 1]
          const isDuplicateCall = lastTurn?.rhythmRan === true &&
            lastTurn?.content === 'Applied Zinsser rhythm variation to selection.'

          if (isDuplicateCall) {
            console.log('[finishStandaloneRhythm] Skipping duplicate turn creation')
            return {
              // Still update the document and clear state, but don't add another turn
              brainstormContent: output.new_doc,
              brainstormCleanContent: stripAnnotations(output.new_doc),
              zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
              noChangesMessage: hasChanges ? null : 'Rhythm already varied in selection',
              rhythmIsProcessing: false,
              rhythmIsThinking: false,
              rhythmStreamingThinking: '',
              workflowStatus: 'idle',
              isStandaloneWorkflow: false,
            }
          }

          // Create a turn representing this Rhythm action
          // No diff viewer - changes are already visible in the Edit tab
          const rhythmTurn: Turn = {
            role: 'assistant',
            content: hasChanges ? 'Applied Zinsser rhythm variation to selection.' : 'Rhythm already varied in selection.',
            timestamp: new Date().toISOString(),
            rhythmRan: true,
            rhythmThinking: state.rhythmStreamingThinking || undefined,
          }

          return {
            gatekeeperTurns: [...state.gatekeeperTurns, rhythmTurn],
            brainstormContent: output.new_doc,
            brainstormCleanContent: stripAnnotations(output.new_doc),
            zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
            noChangesMessage: hasChanges ? null : 'Rhythm already varied in selection',
            // Clear Rhythm streaming state
            rhythmIsProcessing: false,
            rhythmIsThinking: false,
            rhythmStreamingThinking: '',
            workflowStatus: 'idle',
            isStandaloneWorkflow: false,
          }
        }),

      // === Voice standalone workflow actions (toolbar button - full document) ===
      // Clear other micro-editing state for standalone workflow isolation
      startVoiceProcessing: () =>
        set({
          voiceIsProcessing: true,
          voiceHasThinking: true,
          voiceStreamingThinking: '',
          workflowStatus: 'processing',
          isStandaloneWorkflow: true,  // Mark as standalone (no Gatekeeper)
          // Clear other micro-editing state (for standalone workflow isolation)
          cutClutterHasThinking: false,
          cutClutterIsProcessing: false,
          cutClutterStreamingThinking: null,
          strengthenVerbsHasThinking: false,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsStreamingThinking: null,
          checkLogicHasThinking: false,
          checkLogicIsProcessing: false,
          checkLogicStreamingThinking: null,
          rhythmHasThinking: false,
          rhythmIsProcessing: false,
          rhythmStreamingThinking: null,
        }),

      startVoiceThinking: () =>
        set({
          voiceIsThinking: true,
          voiceStreamingThinking: '',
        }),

      updateVoiceThinking: (delta) =>
        set((state) => ({
          voiceStreamingThinking: (state.voiceStreamingThinking || '') + delta,
        })),

      finishVoiceThinking: () =>
        set({
          voiceIsThinking: false,
        }),

      // Standalone Voice workflow (toolbar button - bypasses chat, full document)
      // Creates an assistant turn with the edit results
      finishStandaloneVoice: (output) =>
        set((state) => {
          // Check if any changes were made
          const hasChanges = output.final_diff?.has_changes ?? false

          // Guard: Check if we already added a Voice turn recently (prevent duplicates)
          const lastTurn = state.gatekeeperTurns[state.gatekeeperTurns.length - 1]
          const isDuplicateCall = lastTurn?.voiceRan === true &&
            lastTurn?.content === 'Applied Levine voice transformation to document.'

          if (isDuplicateCall) {
            console.log('[finishStandaloneVoice] Skipping duplicate turn creation')
            return {
              // Still update the document and clear state, but don't add another turn
              brainstormContent: output.new_doc,
              brainstormCleanContent: stripAnnotations(output.new_doc),
              zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
              noChangesMessage: hasChanges ? null : 'Voice already optimized',
              voiceIsProcessing: false,
              voiceIsThinking: false,
              voiceStreamingThinking: '',
              workflowStatus: 'idle',
              isStandaloneWorkflow: false,
            }
          }

          // Create a turn representing this Voice action
          // No diff viewer - changes are already visible in the Edit tab
          const voiceTurn: Turn = {
            role: 'assistant',
            content: hasChanges ? 'Applied Levine voice transformation to document.' : 'Voice already optimized.',
            timestamp: new Date().toISOString(),
            voiceRan: true,
            voiceThinking: state.voiceStreamingThinking || undefined,
          }

          return {
            gatekeeperTurns: [...state.gatekeeperTurns, voiceTurn],
            brainstormContent: output.new_doc,
            brainstormCleanContent: stripAnnotations(output.new_doc),
            zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
            noChangesMessage: hasChanges ? null : 'Voice already optimized',
            // Clear Voice streaming state
            voiceIsProcessing: false,
            voiceIsThinking: false,
            voiceStreamingThinking: '',
            workflowStatus: 'idle',
            isStandaloneWorkflow: false,
          }
        }),

      // === CheckLogic actions (micro editing - Pass 3) ===
      // Clear other micro-editing state for standalone workflow isolation
      startCheckLogicProcessing: () =>
        set({
          checkLogicIsProcessing: true,
          checkLogicHasThinking: true,
          checkLogicStreamingThinking: '',
          workflowStatus: 'processing',
          isStandaloneWorkflow: true,  // Mark as standalone (no Gatekeeper)
          // Clear other micro-editing state (for standalone workflow isolation)
          cutClutterHasThinking: false,
          cutClutterIsProcessing: false,
          cutClutterStreamingThinking: null,
          strengthenVerbsHasThinking: false,
          strengthenVerbsIsProcessing: false,
          strengthenVerbsStreamingThinking: null,
          rhythmHasThinking: false,
          rhythmIsProcessing: false,
          rhythmStreamingThinking: null,
          voiceHasThinking: false,
          voiceIsProcessing: false,
          voiceStreamingThinking: null,
        }),

      startCheckLogicThinking: () =>
        set({
          checkLogicIsThinking: true,
          checkLogicStreamingThinking: '',
        }),

      updateCheckLogicThinking: (delta) =>
        set((state) => ({
          checkLogicStreamingThinking: (state.checkLogicStreamingThinking || '') + delta,
        })),

      finishCheckLogicThinking: () =>
        set({
          checkLogicIsThinking: false,
        }),

      finishCheckLogicProcessing: (output) =>
        set({
          checkLogicChangesMade: output.changes_made,
          checkLogicIsProcessing: false,
          checkLogicIsThinking: false,
          // Don't set workflowStatus to idle - RhythmSound will run next
          // Update canvas with logic-checked document
          brainstormContent: output.zin_doc,
          brainstormCleanContent: output.zin_doc,
        }),

      // Standalone CheckLogic workflow (right-click context menu - bypasses chat)
      // Creates an assistant turn with the edit results, similar to other standalone workflows
      finishStandaloneCheckLogic: (output) =>
        set((state) => {
          // Check if any changes were made
          const hasChanges = output.final_diff?.has_changes ?? false

          // Guard: Check if we already added a CheckLogic turn recently (prevent duplicates)
          const lastTurn = state.gatekeeperTurns[state.gatekeeperTurns.length - 1]
          const isDuplicateCall = lastTurn?.checkLogicRan === true &&
            lastTurn?.content === 'Applied Zinsser logic checking to selection.'

          if (isDuplicateCall) {
            console.log('[finishStandaloneCheckLogic] Skipping duplicate turn creation')
            return {
              // Still update the document and clear state, but don't add another turn
              brainstormContent: output.new_doc,
              brainstormCleanContent: stripAnnotations(output.new_doc),
              zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
              noChangesMessage: hasChanges ? null : 'Logic already checked in selection',
              checkLogicIsProcessing: false,
              checkLogicIsThinking: false,
              checkLogicStreamingThinking: '',
              workflowStatus: 'idle',
              isStandaloneWorkflow: false,
            }
          }

          // Create a turn representing this CheckLogic action
          // No diff viewer - changes are already visible in the Edit tab
          const checkLogicTurn: Turn = {
            role: 'assistant',
            content: hasChanges ? 'Applied Zinsser logic checking to selection.' : 'Logic already checked in selection.',
            timestamp: new Date().toISOString(),
            checkLogicRan: true,
            checkLogicThinking: state.checkLogicStreamingThinking || undefined,
          }

          return {
            gatekeeperTurns: [...state.gatekeeperTurns, checkLogicTurn],
            brainstormContent: output.new_doc,
            brainstormCleanContent: stripAnnotations(output.new_doc),
            zinsserDocAnnotated: hasChanges ? (output.annotated_doc || '') : '',
            noChangesMessage: hasChanges ? null : 'Logic already checked in selection',
            // Clear CheckLogic streaming state
            checkLogicIsProcessing: false,
            checkLogicIsThinking: false,
            checkLogicStreamingThinking: '',
            workflowStatus: 'idle',
            isStandaloneWorkflow: false,
          }
        }),

      // === RhythmSound actions (micro editing - Pass 4) ===
      startRhythmSoundProcessing: () =>
        set({
          rhythmSoundIsProcessing: true,
          rhythmSoundHasThinking: true,
          rhythmSoundStreamingThinking: '',
          workflowStatus: 'processing',
        }),

      startRhythmSoundThinking: () =>
        set({
          rhythmSoundIsThinking: true,
          rhythmSoundStreamingThinking: '',
        }),

      updateRhythmSoundThinking: (delta: string) =>
        set((state) => ({
          rhythmSoundStreamingThinking: (state.rhythmSoundStreamingThinking || '') + delta,
        })),

      finishRhythmSoundThinking: () =>
        set({
          rhythmSoundIsThinking: false,
        }),

      finishRhythmSoundProcessing: (output: { zin_doc: string; changes_made: string[] }) =>
        set({
          // NOTE: Don't update the Gatekeeper turn with node flags here.
          // The Responder will create its own turn with the node flags attached.
          // This keeps the Gatekeeper response clean (just interview/routing).
          rhythmSoundChangesMade: output.changes_made,
          rhythmSoundIsProcessing: false,
          rhythmSoundIsThinking: false,
          // Don't set workflow to idle yet - Responder still needs to run
          // Update canvas with rhythm-edited document
          brainstormContent: output.zin_doc,
          brainstormCleanContent: output.zin_doc,
        }),

      // === Responder actions (coaching response after editing) ===
      startResponderProcessing: () =>
        set({
          responderIsProcessing: true,
          responderHasThinking: true,
          responderStreamingThinking: '',
          workflowStatus: 'processing',
        }),

      startResponderThinking: () =>
        set({
          responderIsThinking: true,
          responderStreamingThinking: '',
        }),

      updateResponderThinking: (delta: string) =>
        set((state) => ({
          responderStreamingThinking: (state.responderStreamingThinking || '') + delta,
        })),

      finishResponderThinking: () =>
        set({
          responderIsThinking: false,
        }),

      finishResponderProcessing: (output: { response: string; follow_up_question?: string | null }) =>
        set((state) => {
          const updatedTurns = [...state.gatekeeperTurns]
          const hasResponderContent = Boolean(output.response && output.response.trim().length > 0)

          // Check if we should attach the diff
          const shouldAttachDiff = state.finalDiff !== null && state.finalDiff.has_changes && hasResponderContent

          if (hasResponderContent) {
            // Add Responder as a NEW turn (stacks below Gatekeeper's response)
            // This preserves the Gatekeeper's message and adds the coaching response
            const responderTurn: Turn = {
              role: 'assistant',
              content: output.response,
              timestamp: new Date().toISOString(),
              responderThinking: state.responderStreamingThinking || undefined,
              responderRan: true,
              // Transfer node execution flags from *HasThinking state (explicitly set when node starts)
              zinsserRan: state.zinsserHasThinking,
              cutClutterRan: state.cutClutterHasThinking,
              strengthenVerbsRan: state.strengthenVerbsHasThinking,
              checkLogicRan: state.checkLogicHasThinking,
              rhythmSoundRan: state.rhythmSoundHasThinking,
              // Transfer thinking content
              zinsserThinking: state.zinsserStreamingThinking || undefined,
              cutClutterThinking: state.cutClutterStreamingThinking || undefined,
              strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
              checkLogicThinking: state.checkLogicStreamingThinking || undefined,
              rhythmSoundThinking: state.rhythmSoundStreamingThinking || undefined,
              // Attach diff to the responder turn
              finalDiff: shouldAttachDiff ? state.finalDiff : undefined,
              showFinalDiff: shouldAttachDiff,
            }
            updatedTurns.push(responderTurn)
          }

          return {
            gatekeeperTurns: updatedTurns,
            responderResponse: output.response,
            responderFollowUpQuestion: output.follow_up_question || null,
            responderIsProcessing: false,
            responderIsThinking: false,
            workflowStatus: 'idle',
            // Clear global finalDiff state since it's now on the turn
            finalDiff: null,
            showFinalDiff: false,
          }
        }),

      // Final diff actions
      // NOTE: setFinalDiff stores the diff but does NOT show it yet
      // The diff is attached to the turn by finishResponderProcessing when the response arrives
      setFinalDiff: (diff) => set({ finalDiff: diff }),

      // Legacy global diff viewer (deprecated - use per-turn diff)
      showFinalDiffViewer: () => set({ showFinalDiff: true }),
      hideFinalDiffViewer: () => set({ showFinalDiff: false }),

      // Hide diff viewer for a specific turn (by turn index)
      hideTurnDiffViewer: (turnIndex: number) =>
        set((state) => {
          const updatedTurns = [...state.gatekeeperTurns]
          if (turnIndex >= 0 && turnIndex < updatedTurns.length) {
            updatedTurns[turnIndex] = {
              ...updatedTurns[turnIndex],
              showFinalDiff: false,
            }
          }
          return { gatekeeperTurns: updatedTurns }
        }),

      // Real-time canvas editing - apply str_replace as edits stream in
      // Wraps new text in {{flash:...}} markers for temporary highlight animation
      applyCanvasEdit: (oldStr, newStr) =>
        set((state) => {
          const currentContent = state.brainstormContent || ''

          // Sanitize AI response — strip any leaked HTML tags (e.g. </span> from annotation marks)
          const cleanNewStr = newStr.replace(/<[^>]+>/g, '')

          // Debug: Log the edit attempt
          console.log('[applyCanvasEdit] Attempting edit:', {
            oldStrLength: oldStr.length,
            newStrLength: newStr.length,
            oldStrPreview: oldStr.slice(0, 80),
            contentLength: currentContent.length,
            hasContent: !!currentContent,
          })

          // Strategy 1: Try exact match first (fast path)
          if (currentContent.includes(oldStr)) {
            const flashId = Date.now().toString(36)
            const markedNewStr = cleanNewStr ? `{{flash:${flashId}}}${cleanNewStr}{{/flash}}` : ''
            const newContent = currentContent.replace(oldStr, markedNewStr)
            console.log('[applyCanvasEdit] Exact match succeeded:', oldStr.slice(0, 50))
            return { brainstormContent: newContent }
          }

          // Strategy 2: Strip flash markers and try again
          // This handles cases where previous edits added flash markers that interfere
          const cleanContent = stripFlashMarkers(currentContent)
          if (cleanContent.includes(oldStr)) {
            // Found in clean content - apply edit to clean version first
            const flashId = Date.now().toString(36)
            const markedNewStr = cleanNewStr ? `{{flash:${flashId}}}${cleanNewStr}{{/flash}}` : ''
            const newContent = cleanContent.replace(oldStr, markedNewStr)
            console.log('[applyCanvasEdit] Match after stripping flash markers:', oldStr.slice(0, 50))
            return { brainstormContent: newContent }
          }

          // No match found - log detailed diagnostics
          console.warn('[applyCanvasEdit] oldStr not found in canvas:', {
            oldStrPreview: oldStr.slice(0, 100),
            oldStrLength: oldStr.length,
            contentLength: currentContent.length,
            contentPreview: currentContent.slice(0, 200),
            // Check for common issues
            hasFlashMarkers: currentContent.includes('{{flash:'),
            hasAnnotations: currentContent.includes('==') || currentContent.includes('~~'),
          })
          return {}
        }),

      // Real-time canvas insert - for creation mode when Zinsser builds from scratch
      // Inserts text after the specified line number (0 = beginning of document)
      applyCanvasInsert: (insertLine, insertText) =>
        set((state) => {
          const currentContent = state.brainstormContent || ''
          const lines = currentContent.split('\n')

          // Add flash markers for animation
          const flashId = Date.now().toString(36)
          const markedText = `{{flash:${flashId}}}${insertText}{{/flash}}`

          // Insert after the specified line (0 = insert at beginning)
          if (insertLine <= 0) {
            // Insert at beginning
            const newContent = currentContent ? markedText + '\n' + currentContent : markedText
            console.log('[applyCanvasInsert] Inserted at beginning:', insertText.slice(0, 50))
            return { brainstormContent: newContent }
          } else if (insertLine >= lines.length) {
            // Append at end
            const newContent = currentContent ? currentContent + '\n' + markedText : markedText
            console.log('[applyCanvasInsert] Appended at end:', insertText.slice(0, 50))
            return { brainstormContent: newContent }
          } else {
            // Insert after specified line
            lines.splice(insertLine, 0, markedText)
            const newContent = lines.join('\n')
            console.log('[applyCanvasInsert] Inserted after line', insertLine, ':', insertText.slice(0, 50))
            return { brainstormContent: newContent }
          }
        }),

      // Annotation toggle - shows/hides <ins>/<del> annotations on the canvas
      toggleAnnotations: () =>
        set((state) => ({
          showAnnotations: !state.showAnnotations,
        })),

      // Set the annotated document (from session_complete event)
      setAnnotatedDoc: (annotatedDoc) =>
        set({
          zinsserDocAnnotated: annotatedDoc,
        }),

      // Attachment version increment (triggers panel refresh after paste upload)
      incrementAttachmentVersion: () =>
        set((state) => ({
          attachmentVersion: state.attachmentVersion + 1,
        })),

      // Anatomy view state
      anatomyStructure: null as ParseResult | null,
      anatomyStructureHash: '',
      anatomyStructureLoading: false,
      anatomyInsightCache: {} as Record<string, AnatomyInsightResponse>,
      anatomyInsightLoading: false,

      fetchAnatomyStructure: async (content: string) => {
        if (!content.trim()) {
          set({ anatomyStructure: null, anatomyStructureHash: '', anatomyStructureLoading: false })
          return
        }
        set({ anatomyStructureLoading: true })
        try {
          const apiResponse = await parseAnatomyStructure(content)
          const result = convertAPIResponse(apiResponse)
          set({
            anatomyStructure: result,
            anatomyStructureHash: result.content_hash,
            anatomyStructureLoading: false,
          })
        } catch (e) {
          console.error('[Anatomy] Failed to parse structure:', e)
          set({ anatomyStructureLoading: false })
        }
      },

      // Instantly patch a sentence in the local anatomy structure (shows edit immediately)
      patchAnatomySentence: (paraIndex: number, sentIndex: number, newText: string) => {
        const current = useZinsserStore.getState().anatomyStructure
        if (!current) return

        const newParagraphs = current.paragraphs.map((para, pi) => {
          if (pi !== paraIndex) return para
          const newSentences = para.sentences.map((sent, si) => {
            if (si !== sentIndex) return sent
            // Replace with uncolored placeholder — spans empty, text updated
            return {
              ...sent,
              text: newText,
              word_count: newText.trim().split(/\s+/).filter(Boolean).length,
              spans: [], // no colors until Haiku re-parses
            }
          })
          return {
            ...para,
            sentences: newSentences,
            rhythm: newSentences.map(s => s.word_count),
          }
        })

        set({
          anatomyStructure: { ...current, paragraphs: newParagraphs },
        })
      },

      // Re-parse only the paragraph that changed (sends just that paragraph to Haiku)
      reparseAnatomyParagraph: async (paraIndex: number) => {
        const current = useZinsserStore.getState().anatomyStructure
        if (!current || !current.paragraphs[paraIndex]) return

        const paraText = current.paragraphs[paraIndex].sentences.map(s => s.text).join(' ')

        try {
          const apiResponse = await parseAnatomyStructure(paraText)
          const result = convertAPIResponse(apiResponse)

          if (result.paragraphs.length > 0) {
            const freshStructure = useZinsserStore.getState().anatomyStructure
            if (!freshStructure) return

            const updatedParagraphs = freshStructure.paragraphs.map((para, pi) =>
              pi === paraIndex ? result.paragraphs[0] : para
            )

            set({
              anatomyStructure: { ...freshStructure, paragraphs: updatedParagraphs },
            })
          }
        } catch (e) {
          console.error('[Anatomy] Failed to reparse paragraph:', e)
        }
      },

      fetchAnatomyInsight: async (text: string, scope: 'sentence' | 'paragraph', context?: string) => {
        const cacheKey = `${scope}:${text}`
        const cached = useZinsserStore.getState().anatomyInsightCache[cacheKey]
        if (cached) return cached
        try {
          const result = await getAnatomyInsight(text, scope, context)
          set((state) => ({
            anatomyInsightCache: { ...state.anatomyInsightCache, [cacheKey]: result },
          }))
          return result
        } catch (e) {
          console.error('Failed to fetch anatomy insight:', e)
          return null
        }
      },

      addAnatomyInsightToChat: async (text: string, scope: 'sentence' | 'paragraph', context?: string) => {
        set({ anatomyInsightLoading: true })
        try {
          const result = await useZinsserStore.getState().fetchAnatomyInsight(text, scope, context)
          if (result?.observations.length) {
            const content = result.observations.join('\n\n')
            const turn: Turn = {
              role: 'assistant',
              content,
              timestamp: new Date().toISOString(),
              isAnatomyInsight: true,
              anatomyScope: scope,
              anatomySourceText: text.length > 100 ? text.slice(0, 100) + '...' : text,
            }
            set((state) => ({
              gatekeeperTurns: [...state.gatekeeperTurns, turn],
              anatomyInsightLoading: false,
            }))
          } else {
            set({ anatomyInsightLoading: false })
          }
        } catch (e) {
          console.error('Failed to add anatomy insight:', e)
          set({ anatomyInsightLoading: false })
        }
      },

      clearAnatomyState: () =>
        set({
          anatomyStructure: null,
          anatomyStructureHash: '',
          anatomyStructureLoading: false,
          anatomyInsightCache: {},
          anatomyInsightLoading: false,
        }),
    }),
    {
      name: 'zinsser-store-v3',  // v3: removed zinsserMode from partialize to fix read-only Canvas
      version: 4,  // Bump version to trigger migration
      migrate: (persistedState: unknown, _version: number) => {
        const state = persistedState as Partial<ZState>

        // CRITICAL: On hydration, strip annotations from content fields
        // Annotations are transient (show what changed during a turn)
        // After page reload, user has implicitly "accepted" all changes
        // So we display clean content without annotation markers

        // Strip annotations from brainstorm content
        if (state.brainstormContent) {
          state.brainstormContent = stripAnnotations(state.brainstormContent)
        }
        if (!state.brainstormCleanContent && state.brainstormContent) {
          state.brainstormCleanContent = state.brainstormContent
        } else if (state.brainstormCleanContent) {
          state.brainstormCleanContent = stripAnnotations(state.brainstormCleanContent)
        }

        // Strip annotations from foolscap content
        if (state.foolscapContent) {
          state.foolscapContent = stripAnnotations(state.foolscapContent)
        }
        if (!state.foolscapCleanContent && state.foolscapContent) {
          state.foolscapCleanContent = state.foolscapContent
        } else if (state.foolscapCleanContent) {
          state.foolscapCleanContent = stripAnnotations(state.foolscapCleanContent)
        }

        // Strip annotations from scratchpad content
        if (state.scratchpadContent) {
          state.scratchpadContent = stripAnnotations(state.scratchpadContent)
        }
        if (!state.scratchpadCleanContent && state.scratchpadContent) {
          state.scratchpadCleanContent = state.scratchpadContent
        } else if (state.scratchpadCleanContent) {
          state.scratchpadCleanContent = stripAnnotations(state.scratchpadCleanContent)
        }

        return state as ZState
      },
      // Persist session state for recovery
      // NOTE: Do NOT persist zinsserMode - it should always default to 'gatekeeper'
      // Persisting it caused Canvas to be read-only when old localStorage had 'brainstorm'
      partialize: (state) => ({
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        mobileView: state.mobileView,
        turns: state.turns,
        currentQueries: state.currentQueries,
        narrativeDirection: state.narrativeDirection,
        keyThemes: state.keyThemes,
        phase: state.phase,
        queryResults: state.queryResults,
        totalCards: state.totalCards,
        // Persist brainstorm state
        brainstormMode: state.brainstormMode,
        brainstormContent: state.brainstormContent,
        brainstormCleanContent: state.brainstormCleanContent,
        brainstormPhase: state.brainstormPhase,
        brainstormWorkingDirection: state.brainstormWorkingDirection,
        brainstormKeyThemes: state.brainstormKeyThemes,
        brainstormGaps: state.brainstormGaps,
        brainstormTurns: state.brainstormTurns,
        // Persist foolscap state
        foolscapContent: state.foolscapContent,
        foolscapCleanContent: state.foolscapCleanContent,
        foolscapPhase: state.foolscapPhase,
        // Persist scratchpad state
        scratchpadContent: state.scratchpadContent,
        scratchpadCleanContent: state.scratchpadCleanContent,
        // Persist gatekeeper state
        gatekeeperTurns: state.gatekeeperTurns,
        // Persist Zinsser state (for showing thinking drawers after refresh)
        zinsserDoc: state.zinsserDoc,
        zinsserDiagnosis: state.zinsserDiagnosis,
        zinsserChangesMade: state.zinsserChangesMade,
        zinsserStreamingThinking: state.zinsserStreamingThinking,
        zinsserHasThinking: state.zinsserHasThinking,
        // Persist CutClutter state (for showing thinking drawers after refresh)
        cutClutterChangesMade: state.cutClutterChangesMade,
        cutClutterStreamingThinking: state.cutClutterStreamingThinking,
        cutClutterHasThinking: state.cutClutterHasThinking,
      }),
      // CRITICAL: After rehydration, verify the data belongs to current user
      // This is called after the store has been rehydrated from localStorage
      onRehydrateStorage: () => {
        return async (_state, error) => {
          if (error) {
            console.error('[zinsser-store] Rehydration error:', error)
            return
          }

          // Import supabase dynamically to avoid circular deps
          const { supabase } = await import('@/lib/supabase')

          // Get current user from Supabase
          const { data: { session } } = await supabase.auth.getSession()
          const currentUserId = session?.user?.id

          // Get stored user ID
          const storedUserId = localStorage.getItem('zinsser-store-user-id')

          // If there's a user mismatch, clear the store
          if (currentUserId && storedUserId && storedUserId !== currentUserId) {
            console.log('[zinsser-store] User mismatch detected, clearing stale data')
            console.log('[zinsser-store] Stored:', storedUserId, 'Current:', currentUserId)

            // Clear localStorage
            localStorage.removeItem('zinsser-store-v3')
            localStorage.setItem('zinsser-store-user-id', currentUserId)

            // Reset the store to initial state
            useZinsserStore.getState().startNewSession()
          } else if (currentUserId && !storedUserId) {
            // First time for this user, store their ID
            localStorage.setItem('zinsser-store-user-id', currentUserId)
          }
        }
      },
    }
  )
)

// Re-export types from api for convenience
export type { QueryResult, RetrievedCard }
